

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { io } from 'socket.io-client';
import api from '../services/api';
import logoIconPng from './logo-icon.svg';

import OSForm from './components/OSForm';
import OSList from './components/OSList';
import OSReports from './components/OSReports';
import InsightsDashboard from './components/InsightsDashboard';
import RoboPosVenda from './components/RoboPosVenda';
import TemplatesManager from './components/TemplatesManager';
import WhatsAppPro from './components/WhatsAppPro';
import RoboConfig from './components/RoboConfig';
import AgentesRobos from './components/AgentesRobos';
import FluxoDeCaixa from './RelaFinaStart';
import DREOtica from './Dre';
import Estoque from './Estoque';
import BackupFotos from './components/BackupFotos';
import SimuladorUltraRealista from './components/SimuladorUltraRealista';

// ============================================================================
// UTILITÁRIOS (INTACTOS)
// ============================================================================
const safeParseUser = () => {
  try { return JSON.parse(localStorage.getItem('vizumUser')) || {}; } catch { return {}; }
};

const normalizarId = (valor) => {
  if (!valor) return '';
  if (typeof valor === 'object') return String(valor._id || valor.id || '');
  return String(valor);
};

const getEmpresaIdFromUser = (user = {}) => {
  return normalizarId(user?.empresaId?._id || user?.empresaId || user?.empresa?._id || user?.empresa?.id || '');
};

const montarParamsEmpresa = (empresaId, options = {}) => {
  if (!empresaId) return {};
  if (options.exato) return { empresaId: String(empresaId) };
  return { empresaMatrizId: String(empresaId), matrizId: String(empresaId), escopo: 'grupo', incluirFiliais: 'true' };
};

const resolverArrayApi = (data, keys = []) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  for (const key of keys) { if (Array.isArray(data?.[key])) return data[key]; }
  return [];
};

const getSocketBaseUrl = () => {
  const base = api.defaults.baseURL || window.location.origin;
  try { return `${new URL(base, window.location.origin).protocol}//${new URL(base, window.location.origin).host}`; } catch { return window.location.origin; }
};

const formatDateLocal = (dateString) => {
  if (!dateString) return '--/--';
  try {
    const [year, month, day] = String(dateString).split('T')[0].split('-');
    if (!year || !month || !day) return dateString;
    return `${day}/${month}/${String(year).slice(2)}`;
  } catch { return dateString; }
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value || 0));
};

const getFirstName = (fullName = '') => String(fullName || '').trim().split(' ')[0] || 'Usuário';

const getInitials = (name = '') => {
  const parts = String(name || '').trim().split(' ').filter(Boolean);
  if (!parts.length) return 'US';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
};

const normalizarTextoBusca = (valor = '') => String(valor || '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

const getEmpresaObjectId = (empresa = {}) => normalizarId(empresa?._id || empresa?.id || empresa?.empresaId?._id || empresa?.empresaId || empresa?.unidadeId?._id || empresa?.unidadeId || '');

const getEmpresaLabel = (empresa = {}) => {
  const codigoLoja = empresa?.codigoLoja || empresa?.loja || empresa?.slug || empresa?.codigo || '';
  if (codigoLoja) return String(codigoLoja).trim();
  return String(empresa?.unidade || empresa?.nomeUnidade || empresa?.nome || empresa?.razaoSocial || empresa?.label || 'Loja').trim();
};

const getEmpresaCodigoLoja = (empresa = {}) => String(empresa?.codigoLoja || empresa?.loja || empresa?.slug || empresa?.codigo || '').trim();

const encontrarEmpresaPorIdentidade = (identificadores = [], empresasCadastradas = []) => {
  const ids = identificadores.map(normalizarId).filter(Boolean);
  if (!ids.length) return null;
  return empresasCadastradas.find((empresa) => [empresa?._id, empresa?.id, empresa?.empresaId, empresa?.unidadeId, empresa?.empresa?._id, empresa?.empresa?.id].map(normalizarId).some((id) => ids.includes(id))) || null;
};

const encontrarEmpresaPorTexto = (texto = '', empresasCadastradas = []) => {
  const busca = normalizarTextoBusca(texto);
  if (!busca) return null;
  return empresasCadastradas.find((empresa) => [empresa?.codigoLoja, empresa?.loja, empresa?.unidade, empresa?.nomeUnidade, empresa?.nome, empresa?.razaoSocial, empresa?.label].map(normalizarTextoBusca).some((campo) => campo && (campo === busca || campo.includes(busca) || busca.includes(campo)))) || null;
};

const classificarStatusOperacional = (os = {}) => {
  const status = normalizarTextoBusca(`${os?.status || ''} ${os?.statusmsg || ''}`);
  if (status.includes('ENTREG')) return { key: 'entregue', name: 'Entregues', helper: 'Finalizadas para o cliente', color: CHART_COLORS.success };
  if (status.includes('PRONTO')) return { key: 'prontoLoja', name: 'Pronto na loja', helper: 'Aguardando retirada', color: CHART_COLORS.info };
  if (status.includes('LABORATORIO') || status.includes('LAB')) return { key: 'laboratorio', name: 'No laboratório', helper: 'Em produção técnica', color: CHART_COLORS.warning };
  if (status.includes('PEDIDO') || status.includes('LENTE') || status.includes('ENVIAD')) return { key: 'pedidoLente', name: 'Pedido de lente', helper: 'Pedido/compra em andamento', color: CHART_COLORS.primary };
  if (status.includes('CANCEL')) return { key: 'cancelado', name: 'Canceladas', helper: 'O.S. canceladas', color: CHART_COLORS.neutral };
  return { key: 'outros', name: 'Outros status', helper: 'Sem classificação específica', color: CHART_COLORS.neutral };
};

const isMesmoDia = (dateLike, base = new Date()) => {
  if (!dateLike) return false;
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return false;
  return d.toISOString().slice(0, 10) === base.toISOString().slice(0, 10);
};

const normalizarLojaOS = (os, empresasCadastradas = []) => {
  const empresaPorId = encontrarEmpresaPorIdentidade([os?.empresaId?._id, os?.empresaId, os?.unidadeId?._id, os?.unidadeId, os?.empresa?._id, os?.empresa?.id], empresasCadastradas);
  if (empresaPorId) return getEmpresaLabel(empresaPorId);

  const lojaBruta = os?.loja || os?.unidade || os?.empresa?.codigoLoja || os?.empresa?.unidade || os?.empresa?.nome || os?.empresa || os?.codigoLoja || os?.unidadeNome || 'Principal';
  const empresaPorTexto = encontrarEmpresaPorTexto(lojaBruta, empresasCadastradas);
  if (empresaPorTexto) return getEmpresaLabel(empresaPorTexto);

  const busca = normalizarTextoBusca(lojaBruta);
  if (busca.includes('V01') || busca.includes('LOJA 01')) return 'Vizum 01';
  if (busca.includes('V02') || busca.includes('LOJA 02')) return 'Vizum 02';
  return String(lojaBruta || 'Loja').trim();
};

const resolverNomeVendedorOS = (os = {}) => {
  const candidato = os?.vendedor?.nomeCompleto || os?.vendedor?.nome || os?.vendedorNome || os?.usuario?.nomeCompleto || os?.usuario?.nome || os?.createdBy?.nomeCompleto || os?.createdBy?.nome || os?.vendedor || os?.usuario || '';
  return String(candidato || '').trim() || 'Não informado';
};

// ============================================================================
// WHATSAPP MULTICANAL (INTACTO)
// ============================================================================
const WHATSAPP_STATUS = { CHECKING: 'checking', LOADING: 'loading', QRCODE: 'qrcode', CONNECTED: 'connected', DISCONNECTED: 'disconnected', DISCONNECTING: 'disconnecting' };

const normalizarStatusBackend = (status, connected = false) => {
  const s = String(status || '').toUpperCase();
  if (connected === true || s === 'CONNECTED' || s === 'INCHAT' || s === 'CHATSAVAILABLE') return WHATSAPP_STATUS.CONNECTED;
  if (s === 'QRCODE' || s === 'QR_CODE' || s === 'QR') return WHATSAPP_STATUS.QRCODE;
  if (s === 'INITIALIZING' || s === 'LOADING') return WHATSAPP_STATUS.LOADING;
  if (s === 'DISCONNECTING') return WHATSAPP_STATUS.DISCONNECTING;
  return WHATSAPP_STATUS.DISCONNECTED;
};

const normalizarQrCode = (qr) => {
  if (!qr) return '';
  const value = String(qr);
  if (value.startsWith('data:image') || value.startsWith('http')) return value;
  return value;
};

const normalizarCanalWhatsapp = (canal = {}, empresaIdFallback = '') => {
  const id = normalizarId(canal._id) || normalizarId(canal.id) || normalizarId(canal.canalWhatsappId) || normalizarId(canal.canalId);
  const runtime = canal.runtime || {};
  const rawStatus = runtime.status || canal.status || canal.state || '';
  const connected = runtime.connected === true || canal.connected === true || rawStatus === 'CONNECTED';
  const status = normalizarStatusBackend(rawStatus, connected);

  const empresaId = normalizarId(canal.empresaId?._id || canal.empresaId || canal.empresa?._id || canal.empresaOperacionalId || empresaIdFallback);
  const unidadeId = normalizarId(canal.unidadeId?._id || canal.unidadeId || canal.unidadeOperacionalId || empresaId);
  const empresaMatrizId = normalizarId(canal.empresaMatrizId?._id || canal.empresaMatrizId || canal.empresaOrigemId?._id || empresaIdFallback);
  const grupoEmpresaId = normalizarId(canal.grupoEmpresaId || canal.checkoutSessionId || canal.assinatura?.checkoutSessionId || empresaMatrizId || empresaIdFallback);

  return {
    raw: canal, id, empresaId, unidadeId, empresaMatrizId, grupoEmpresaId,
    nome: canal.nome || canal.label || canal.sessionName || `Canal ${id ? id.slice(-6) : ''}`,
    unidadeNome: canal.unidadeId?.unidade || canal.unidadeId?.nome || canal.empresaId?.unidade || canal.unidadeNome || canal.nomeUnidade || canal.loja || 'Unidade principal',
    codigoLoja: canal.codigoLoja || canal.loja || canal.unidadeId?.codigoLoja || canal.empresaId?.codigoLoja || '',
    numero: canal.numero || canal.telefone || canal.whatsapp || '',
    principal: canal.principal === true || canal.isDefault === true,
    sessionName: runtime.sessionName || canal.sessionName || '',
    status, connected: status === WHATSAPP_STATUS.CONNECTED,
    qrCode: runtime.qrCode || canal.qrCode || canal.qrcode || canal.qr || null
  };
};

const getWhatsappStatusLabel = (status, canais = []) => {
  if (status === WHATSAPP_STATUS.CONNECTED) {
    const online = canais.filter((c) => c.connected).length;
    return online > 1 ? `${online} canais ativos` : 'Sessão ativa';
  }
  if (status === WHATSAPP_STATUS.LOADING) return 'Inicializando...';
  if (status === WHATSAPP_STATUS.QRCODE) return 'Aguardando leitura';
  if (status === WHATSAPP_STATUS.DISCONNECTING) return 'Desconectando...';
  if (status === WHATSAPP_STATUS.CHECKING) return 'Verificando...';
  return 'Desconectado';
};

const getWhatsappButtonLabel = (status) => {
  if (status === WHATSAPP_STATUS.LOADING) return 'Inicializando...';
  if (status === WHATSAPP_STATUS.QRCODE) return 'Aguardando leitura';
  if (status === WHATSAPP_STATUS.DISCONNECTING) return 'Desconectando...';
  return 'Canais de WhatsApp';
};

// ============================================================================
// CONFIG VISUAL (NOVA PALETA DE CORES NO CSS, MAS MANTENDO A ESTRUTURA AQUI)
// ============================================================================
const CHART_COLORS = { primary: '#3B82F6', success: '#10B981', warning: '#F59E0B', info: '#0EA5E9', danger: '#EF4444', neutral: '#64748B' };

const Icons = {
  Menu: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" /></svg>,
  ChevronDown: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>,
  Calendar: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>,
  Dashboard: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /></svg>,
  Plus: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
  List: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>,
  Chart: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><path d="M18 17V9" /><path d="M13 17V5" /><path d="M8 17v-3" /></svg>,
  TrendingUp: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13 16 9 12 2 19" /><polyline points="16 7 22 7 22 13" /></svg>,
  Close: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
  Robot: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2" /><circle cx="12" cy="5" r="2" /><path d="M12 7v4" /><line x1="8" y1="16" x2="8.01" y2="16" /><line x1="16" y1="16" x2="16.01" y2="16" /></svg>,
  Edit: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" /></svg>,
  Message: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>,
  Box: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>,
  Search: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>,
  Mic: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="22" /></svg>,
  Wallet: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" /><path d="M3 5v14a2 2 0 0 0 2 2h16v-5" /><path d="M18 12a2 2 0 0 0 0 4h4v-4Z" /></svg>,
  PieChart: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.21 15.89A10 10 0 1 1 8 2.83" /><path d="M22 12A10 10 0 0 0 12 2v10z" /></svg>,
  Bolt: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>,
  Filter: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>,
  Store: () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M3 21h18v-8a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v8z" /><path d="M7 21v-8h10v8" /><path d="M5 11V7l2-4h10l2 4v4" /><path d="M5 7h14" /></svg>,
  Qr: () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><path d="M14 14h2v2h-2z" /><path d="M18 14h3v3" /><path d="M14 18h3v3" /><path d="M19 19h2v2" /></svg>,
  LogOut: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>,
  Eye: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>,
  EyeOff: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.94 10.94 0 0 1 12 19C5 19 1 12 1 12a20.3 20.3 0 0 1 5.06-5.94" /><path d="M9.9 4.24A10.6 10.6 0 0 1 12 4c7 0 11 8 11 8a20.8 20.8 0 0 1-3.17 4.19" /><path d="M14.12 14.12A3 3 0 0 1 9.88 9.88" /><path d="M1 1l22 22" /></svg>,
  Birthday: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 11h16v10H4z" /><path d="M4 15c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2" /><path d="M8 11V8M12 11V8M16 11V8" /><path d="M8 5h.01M12 5h.01M16 5h.01" /></svg>
};

const NAV_SECTIONS = [
  {
    key: 'principal',
    label: 'Início',
    helper: 'Acesso rápido',
    items: [
      { key: 'home', label: 'Home', icon: <Icons.Dashboard />, destaque: true, roles: ['admin', 'vendedor'] },
      { key: 'insights', label: 'Metas e insights', icon: <Icons.TrendingUp />, roles: ['admin'] },
      { key: 'nova', label: 'Registrar nova venda', icon: <Icons.Plus />, destaque: true, roles: ['admin', 'vendedor'] }
    ]
  },
  {
    key: 'relatorios-menu',
    label: 'Relatórios',
    helper: 'Consulta de vendas e O.S.',
    items: [
      { key: 'relatorios', label: 'Relatório detalhado', icon: <Icons.Chart />, roles: ['admin', 'vendedor'] },
      { key: 'lista', label: 'Relatório padrão', icon: <Icons.List />, roles: ['admin', 'vendedor'] }
    ]
  },
  {
    key: 'pos-venda',
    label: 'Relacionamento',
    helper: 'WhatsApp e pós-venda',
    special: true,
    items: [
      { key: 'whatsapp', label: 'Chat WhatsApp', icon: <Icons.Message />, destaque: true, roles: ['admin', 'vendedor'] },
      { key: 'robo', label: 'Ativador manual de pós-venda', icon: <Icons.Robot />, roles: ['admin'] },
      { key: 'templates', label: 'Configurar mensagens prontas', icon: <Icons.Edit />, roles: ['admin'] },
      { key: 'config-vendas', label: 'Enviar resumos via WhatsApp', icon: <Icons.Bolt />, roles: ['admin'] }
    ]
  },
  {
    key: 'agentes-menu',
    label: 'Agentes',
    helper: 'Automações programadas',
    roles: ['admin'],
    items: [
      { key: 'agentes', label: 'Agentes programados', icon: <Icons.Robot />, roles: ['admin'] }
    ]
  },
  {
    key: 'gestao',
    label: 'Gestão',
    helper: 'Financeiro e estoque',
    roles: ['admin'],
    items: [
      { key: 'caixa', label: 'Fluxo de Caixa', icon: <Icons.Wallet />, roles: ['admin'] },
      { key: 'dre', label: 'DRE', icon: <Icons.PieChart />, roles: ['admin'] },
      { key: 'estoque', label: 'Estoque', icon: <Icons.Box />, roles: ['admin'] }
    ]
  }
];

const normalizarTipoUsuario = (user = {}) => String(user?.tipo || user?.role || user?.perfil || 'vendedor').trim().toLowerCase();

const usuarioPodeVer = (item = {}, tipoUsuario = 'vendedor') => {
  const roles = item.roles || item.allowedRoles || null;
  if (!roles || !roles.length) return true;
  return roles.includes(tipoUsuario);
};

const filtrarMenuPorPerfil = (sections = [], tipoUsuario = 'vendedor') => {
  return sections
    .filter((section) => usuarioPodeVer(section, tipoUsuario))
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => usuarioPodeVer(item, tipoUsuario))
    }))
    .filter((section) => section.items.length > 0);
};

const getPrimeiraRotaPermitida = (sections = []) => sections?.[0]?.items?.[0]?.key || 'home';

const getRouteMeta = (sections = [], route = 'home') => {
  for (const section of sections) {
    const item = section.items.find((navItem) => navItem.key === route);
    if (item) {
      return {
        ...item,
        sectionLabel: section.label,
        sectionHelper: section.helper || ''
      };
    }
  }
  return {
    key: 'home',
    label: 'Home',
    sectionLabel: 'Início',
    sectionHelper: 'Acompanhe a operação da loja'
  };
};

const NavItem = ({ icon, label, active, onClick, collapsed, destaque }) => (
  <button
    type="button"
    className={`nav-item ${active ? 'active' : ''} ${collapsed ? 'collapsed' : ''} ${destaque ? 'featured' : ''}`}
    onClick={onClick}
    title={collapsed ? label : ''}
    aria-current={active ? 'page' : undefined}
  >
    <div className="nav-icon">{icon}</div>
    {!collapsed && <span className="nav-label">{label}</span>}
    {active && !collapsed && <div className="active-indicator" />}
  </button>
);

const SPLASH_MESSAGES = [
  'Preparando o ambiente...',
  'Carregando ordens de serviço...',
  'Sincronizando calendário...',
  'Organizando dados da loja...'
];

const CleanMetric = ({ title, value, subtitle, tone = 'blue' }) => (
  <div className={`clean-metric-card ${tone}`}>
    <div className="metric-header">
      <span>{title}</span>
      <div className={`metric-icon-bg ${tone}`} />
    </div>
    <strong>{value}</strong>
    <p>{subtitle}</p>
  </div>
);

const DASH_WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const DASH_MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const toDateKey = (dateLike = new Date()) => {
  const d = dateLike instanceof Date ? new Date(dateLike) : new Date(dateLike);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const addDias = (base = new Date(), dias = 0) => {
  const d = new Date(base);
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + dias);
  return d;
};

const getOSPrimeiroItem = (os = {}) => {
  if (Array.isArray(os?.itens) && os.itens.length) return os.itens[0] || {};
  if (Array.isArray(os?.items) && os.items.length) return os.items[0] || {};
  return os?.item || {};
};

const getOSLenteResumo = (os = {}) => {
  const item = getOSPrimeiroItem(os);
  return (
    item?.produto?.lente ||
    item?.lente ||
    os?.produto?.lente ||
    os?.lente ||
    os?.lenteNome ||
    'Lente não informada'
  );
};

const getOSWhatsappResumo = (os = {}) => {
  return String(
    os?.whatsapp ||
    os?.cliente?.whatsapp ||
    os?.cliente?.telefone ||
    os?.cliente?.celular ||
    os?.telefone ||
    ''
  ).trim();
};

const getOSValorResumo = (os = {}) => {
  return Number(os?.valorTotal || os?.pagamento?.valorTotal || os?.pagamento?.total || os?.sinal || 0);
};

const getOSClienteNome = (os = {}) => (
  os?.cliente?.nome ||
  os?.cliente?.nomeCompleto ||
  os?.paciente?.nome ||
  os?.paciente ||
  `O.S. #${os?.numeroOS || '---'}`
);

const getOSAnexos = (os = {}) => {
  if (Array.isArray(os?.anexos)) return os.anexos.filter(Boolean);
  if (Array.isArray(os?.fotos)) return os.fotos.filter(Boolean);
  if (Array.isArray(os?.imagens)) return os.imagens.filter(Boolean);
  return [];
};

const getOSPrimeiraFoto = (os = {}) => {
  const direto =
    os?.primeiraFoto ||
    os?.primeiroAnexo ||
    os?.fotoPrincipal ||
    os?.thumbnailUrl ||
    os?.thumbUrl ||
    os?.anexoPrincipal ||
    os?.preview?.foto ||
    '';

  if (typeof direto === 'string' && direto.trim()) return direto;

  if (direto && typeof direto === 'object') {
    return direto.url || direto.path || direto.caminho || direto.imageUrl || '';
  }

  const primeiroAnexo = getOSAnexos(os)[0];

  if (typeof primeiroAnexo === 'string') return primeiroAnexo;

  if (primeiroAnexo && typeof primeiroAnexo === 'object') {
    return (
      primeiroAnexo.url ||
      primeiroAnexo.path ||
      primeiroAnexo.caminho ||
      primeiroAnexo.imageUrl ||
      ''
    );
  }

  return '';
};

const formatDashboardDate = (dateKey = '') => {
  if (!dateKey) return '--/--';
  const [ano, mes, dia] = String(dateKey).split('-').map(Number);
  if (!ano || !mes || !dia) return formatDateLocal(dateKey);
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short' }).format(new Date(ano, mes - 1, dia));
};

const isStatusEntregue = (os = {}) => normalizarTextoBusca(`${os?.status || ''} ${os?.statusmsg || ''}`).includes('ENTREG');

const normalizarLojaAgendamento = (agendamento = {}, empresasCadastradas = []) => {
  const empresa = encontrarEmpresaPorIdentidade(
    [agendamento?.empresaId?._id, agendamento?.empresaId, agendamento?.unidadeId?._id, agendamento?.unidadeId],
    empresasCadastradas
  );

  if (empresa) return getEmpresaLabel(empresa);

  return String(
    agendamento?.codigoLoja ||
    agendamento?.loja ||
    agendamento?.unidadeNome ||
    agendamento?.empresaNome ||
    'Loja'
  ).trim();
};

const getAgendamentoTipoLabel = (tipo = '') => {
  const key = String(tipo || '').toLowerCase();
  if (key === 'exame') return 'Exame de vista';
  if (key === 'retirada') return 'Retirada de óculos';
  if (key === 'ajuste') return 'Ajuste / manutenção';
  return 'Atendimento ao cliente';
};

const getAgendamentoStatusLabel = (status = '') => {
  const key = String(status || '').toLowerCase();
  if (key === 'confirmado') return 'Confirmado';
  if (key === 'na_loja') return 'Na loja';
  if (key === 'concluido') return 'Concluído';
  if (key === 'cancelado') return 'Cancelado';
  return 'Agendado';
};

const getAgendamentoTone = (status = '') => {
  const key = String(status || '').toLowerCase();
  if (key === 'concluido') return 'green';
  if (key === 'na_loja') return 'purple';
  return 'blue';
};

const getAniversarianteNome = (cliente = {}) => String(
  cliente?.nome || cliente?.nomeCompleto || cliente?.clienteNome || cliente?.paciente || 'Cliente'
).trim();

const getCalendarDays = (baseDate = new Date()) => {
  const ano = baseDate.getFullYear();
  const mes = baseDate.getMonth();
  const primeiroDia = new Date(ano, mes, 1).getDay();
  const totalDias = new Date(ano, mes + 1, 0).getDate();
  return [
    ...Array.from({ length: primeiroDia }, (_, i) => ({ key: `empty-${i}`, empty: true })),
    ...Array.from({ length: totalDias }, (_, i) => {
      const dia = i + 1;
      const date = new Date(ano, mes, dia, 12);
      return { key: toDateKey(date), dateKey: toDateKey(date), dia, empty: false };
    })
  ];
};

const getCanalStatusMeta = (canal = {}) => {
  if (canal.connected || canal.status === WHATSAPP_STATUS.CONNECTED) return { label: 'Conectado', tone: 'online' };
  if (canal.status === WHATSAPP_STATUS.QRCODE) return { label: 'Aguardando QR', tone: 'qr' };
  if (canal.status === WHATSAPP_STATUS.LOADING) return { label: 'Inicializando', tone: 'loading' };
  if (canal.status === WHATSAPP_STATUS.DISCONNECTING) return { label: 'Desconectando', tone: 'loading' };
  return { label: 'Desconectado', tone: 'offline' };
};

const WhatsappChannelSelectorModal = ({ canais, loading, canalEmAcaoId, onClose, onConnect, onManage }) => (
  <div className="modal-backdrop fade-in">
    <div className="modal-box channel-selector-modal slide-up">
      <div className="modal-top channel-selector-top">
        <div className="modal-titles">
          <h3>Canais de WhatsApp</h3>
          <p>Escolha o canal que deseja configurar.</p>
        </div>
        <button onClick={onClose} className="icon-btn-close" type="button"><Icons.Close /></button>
      </div>
      <div className="channel-selector-list custom-scrollbar">
        {loading ? (
          <div className="channel-selector-empty">
            <div className="loader-spinner-blue" />
            <span>Carregando canais...</span>
          </div>
        ) : canais.length ? (
          canais.map((canal) => {
            const status = getCanalStatusMeta(canal);
            const emAcao = canalEmAcaoId === canal.id;
            return (
              <article className="channel-selector-item" key={canal.id}>
                <div className={`channel-selector-status-dot ${status.tone}`} />
                <div className="channel-selector-copy">
                  <strong>{canal.nome || 'Canal WhatsApp'}</strong>
                  <span>{canal.unidadeNome || 'Unidade principal'}</span>
                  <small>{canal.numero || canal.sessionName || 'Número não informado'} · {status.label}</small>
                </div>
                <div className="channel-selector-actions">
                  {canal.connected ? (
                    <button type="button" className="channel-action secondary" onClick={() => onManage(canal)} disabled={emAcao}>
                      {emAcao ? 'Aguarde...' : 'Gerenciar'}
                    </button>
                  ) : (
                    <button type="button" className="channel-action primary" onClick={() => onConnect(canal)} disabled={emAcao}>
                      {emAcao ? 'Abrindo...' : 'Conectar'}
                    </button>
                  )}
                </div>
              </article>
            );
          })
        ) : (
          <div className="channel-selector-empty">
            <Icons.Message />
            <strong>Nenhum canal de atendimento cadastrado</strong>
            <span>Crie o canal da loja na tela de Empresas antes de usar o Chat WhatsApp.</span>
            <button type="button" className="channel-action secondary" onClick={onManage}>Abrir empresas</button>
          </div>
        )}
      </div>
      <div className="channel-selector-footer">
        <button type="button" className="channel-action secondary" onClick={onManage}>Configurar canais</button>
      </div>
    </div>
  </div>
);

// ============================================================================
// WORKSPACE UNIFICADO DOS MÓDULOS
// ============================================================================
const ModuleWorkspace = ({ children }) => (
  <div className="module-workspace fade-in">
    <div className="module-workspace-body">{children}</div>
  </div>
);

const DeliveryClientAvatar = ({ os, className = '', onClick }) => {
  const [fotoPath, setFotoPath] = useState(() => getOSPrimeiraFoto(os));
  const [objectUrl, setObjectUrl] = useState('');
  const [erro, setErro] = useState(false);
  const nome = getOSClienteNome(os);

  useEffect(() => {
    let ativo = true;

    setFotoPath(getOSPrimeiraFoto(os));
    setObjectUrl('');
    setErro(false);

    const buscarFotoNoDetalhe = async () => {
      if (getOSPrimeiraFoto(os)) return;

      const id = os?._id || os?.id;
      if (!id) return;

      try {
        const response = await api.get(`/ordens-servico/${id}`);
        const foto = getOSPrimeiraFoto(response?.data || {});

        if (ativo && foto) setFotoPath(foto);
      } catch (error) {
        console.warn('[Dashboard] Não foi possível buscar a primeira foto da OS:', error);
      }
    };

    buscarFotoNoDetalhe();

    return () => {
      ativo = false;
    };
  }, [os]);

  useEffect(() => {
    let ativo = true;
    let urlCriada = '';

    const carregarImagem = async () => {
      if (!fotoPath) return;

      try {
        setErro(false);

        if (
          String(fotoPath).startsWith('data:') ||
          String(fotoPath).startsWith('blob:') ||
          String(fotoPath).startsWith('http://') ||
          String(fotoPath).startsWith('https://')
        ) {
          if (ativo) setObjectUrl(fotoPath);
          return;
        }

        const response = await api.get('/ordens-servico/anexo-imagem', {
          params: { path: fotoPath },
          responseType: 'blob'
        });

        urlCriada = URL.createObjectURL(response.data);

        if (ativo) setObjectUrl(urlCriada);
      } catch (error) {
        console.warn('[Dashboard] Não foi possível carregar a foto protegida:', error);
        if (ativo) setErro(true);
      }
    };

    carregarImagem();

    return () => {
      ativo = false;
      if (urlCriada) URL.revokeObjectURL(urlCriada);
    };
  }, [fotoPath]);

  const conteudo = objectUrl && !erro ? (
    <img src={objectUrl} alt={nome} loading="lazy" />
  ) : (
    <span>{getInitials(nome)}</span>
  );

  if (onClick) {
    return (
      <button type="button" className={`delivery-client-avatar ${className}`} onClick={onClick} title={nome}>
        {conteudo}
      </button>
    );
  }

  return (
    <div className={`delivery-client-avatar ${className}`} title={nome}>
      {conteudo}
    </div>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================
export default function OrdensServico() {
  const [isGlobalLoading, setIsGlobalLoading] = useState(true);
  const [splashStage, setSplashStage] = useState('active');
  const [splashMessageIndex, setSplashMessageIndex] = useState(0);
  const splashMountTime = useRef(Date.now());

  const [modo, setModo] = useState('home');
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [toast, setToast] = useState(null);
  const [targetOsId, setTargetOsId] = useState(null);
  const [todasOS, setTodasOS] = useState([]);
  const [diasFiltro] = useState(3);

  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isListening, setIsListening] = useState(false);

  const [whatsappStatus, setWhatsappStatus] = useState(WHATSAPP_STATUS.CHECKING);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [whatsappSession, setWhatsappSession] = useState({ empresaId: '', sessionName: '', canalWhatsappId: '', canalNome: '' });
  const [whatsappCanais, setWhatsappCanais] = useState([]);
  const [isLoadingWhatsappCanais, setIsLoadingWhatsappCanais] = useState(false);
  const [showWhatsappChannelModal, setShowWhatsappChannelModal] = useState(false);
  const [whatsappCanalEmAcaoId, setWhatsappCanalEmAcaoId] = useState('');

  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [monthlySales, setMonthlySales] = useState(null);
  const [lojaAtiva, setLojaAtiva] = useState('Todas');
  const [dashboardCalendarDate, setDashboardCalendarDate] = useState(() => new Date());
  const [dashboardSelectedDate, setDashboardSelectedDate] = useState(() => toDateKey(new Date()));
  const [aniversariantesHoje, setAniversariantesHoje] = useState([]);
  const [aniversariosEnviadosHoje, setAniversariosEnviadosHoje] = useState(false);
  const [loadingAniversariantes, setLoadingAniversariantes] = useState(false);
  const [agendamentosDashboard, setAgendamentosDashboard] = useState([]);
  const [loadingAgendamentosDashboard, setLoadingAgendamentosDashboard] = useState(false);
  const [erroAgendamentosDashboard, setErroAgendamentosDashboard] = useState('');

  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const profileMenuRef = useRef(null);
  const lastWhatsappEventRef = useRef('');

  const user = useMemo(() => safeParseUser(), []);
  const tipoUsuario = useMemo(() => normalizarTipoUsuario(user), [user]);
  const menuPermitido = useMemo(() => filtrarMenuPorPerfil(NAV_SECTIONS, tipoUsuario), [tipoUsuario]);
  const rotaAtual = useMemo(() => getRouteMeta(menuPermitido, modo), [menuPermitido, modo]);
  const empresaIdAtual = useMemo(() => getEmpresaIdFromUser(user), [user]);
  const empresasDoUsuario = useMemo(() => user?.empresas || user?.empresasCadastradas || user?.lojas || [], [user]);

  const userName = getFirstName(user.nomeCompleto || user.nome || '');
  const userInitials = getInitials(user.nomeCompleto || user.nome || '');
  const socketUrl = useMemo(() => getSocketBaseUrl(), []);

  useEffect(() => {
    if (splashStage === 'hidden') return undefined;
    const timer = setInterval(() => {
      setSplashMessageIndex((prev) => (prev + 1) % SPLASH_MESSAGES.length);
    }, 900);
    return () => clearInterval(timer);
  }, [splashStage]);

  // O splash acompanha somente o carregamento real. Nada de espera artificial.
  useEffect(() => {
    if (isGlobalLoading) return undefined;

    setSplashStage('fading');
    const hideTimer = window.setTimeout(() => setSplashStage('hidden'), 220);
    return () => window.clearTimeout(hideTimer);
  }, [isGlobalLoading]);

  const empresasCadastradas = useMemo(() => {
    const mapa = new Map();
    const adicionarEmpresa = (empresa = {}) => {
      const id = getEmpresaObjectId(empresa);
      const codigo = getEmpresaCodigoLoja(empresa);
      const label = getEmpresaLabel(empresa);
      const chave = id || normalizarTextoBusca(codigo || label);
      if (!chave) return;
      const existente = mapa.get(chave) || {};
      mapa.set(chave, {
        ...existente, ...empresa,
        _id: id || existente._id || empresa._id,
        id: id || existente.id || empresa.id,
        codigoLoja: codigo || existente.codigoLoja || empresa.codigoLoja || empresa.loja || '',
        unidade: empresa.unidade || empresa.nomeUnidade || existente.unidade || label,
        nome: empresa.nome || existente.nome || label
      });
    };
    empresasDoUsuario.forEach(adicionarEmpresa);
    whatsappCanais.forEach((canal) => {
      adicionarEmpresa({
        _id: canal.empresaId || canal.unidadeId, id: canal.empresaId || canal.unidadeId,
        empresaId: canal.empresaId, unidadeId: canal.unidadeId,
        empresaMatrizId: canal.empresaMatrizId || empresaIdAtual,
        codigoLoja: canal.codigoLoja || canal.raw?.codigoLoja || canal.raw?.loja || '', loja: canal.codigoLoja || '',
        unidade: canal.unidadeNome || canal.nome || 'Unidade', nome: canal.unidadeNome || canal.nome || 'Unidade'
      });
    });
    todasOS.forEach((os) => {
      adicionarEmpresa({
        _id: normalizarId(os?.empresaId?._id || os?.empresaId || os?.unidadeId), id: normalizarId(os?.empresaId?._id || os?.empresaId || os?.unidadeId),
        empresaId: normalizarId(os?.empresaId?._id || os?.empresaId), unidadeId: normalizarId(os?.unidadeId?._id || os?.unidadeId || os?.empresaId),
        empresaMatrizId: normalizarId(os?.empresaMatrizId?._id || os?.empresaMatrizId || empresaIdAtual),
        codigoLoja: os?.codigoLoja || os?.loja || os?.empresa?.codigoLoja || '', loja: os?.loja || os?.codigoLoja || '',
        unidade: os?.unidade || os?.unidadeNome || os?.loja || os?.empresa?.unidade || os?.empresa?.nome || '', nome: os?.empresa?.nome || os?.loja || os?.codigoLoja || ''
      });
    });
    return Array.from(mapa.values());
  }, [empresasDoUsuario, whatsappCanais, todasOS, empresaIdAtual]);

  const canalPrincipal = useMemo(() => {
    if (!whatsappCanais.length) return null;
    return whatsappCanais.find((canal) => canal.principal) || whatsappCanais.find((canal) => canal.connected) || whatsappCanais[0];
  }, [whatsappCanais]);

  const whatsappStatusTexto = useMemo(() => getWhatsappStatusLabel(whatsappStatus, whatsappCanais), [whatsappStatus, whatsappCanais]);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    if (navigator.vibrate) navigator.vibrate(35);
    setTimeout(() => setToast(null), 3500);
  }, []);

  const closeMenus = useCallback(() => {
    setSidebarOpen(false); setIsSearchFocused(false); setShowProfileMenu(false); setShowWhatsappChannelModal(false);
  }, []);

  const navigate = useCallback((newMode) => {
    const rotasPermitidas = menuPermitido.flatMap((section) => section.items.map((item) => item.key));
    const rotaFinal = rotasPermitidas.includes(newMode) ? newMode : getPrimeiraRotaPermitida(menuPermitido);
    if (rotaFinal !== 'relatorios') setTargetOsId(null);
    setModo(rotaFinal);
    closeMenus();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [closeMenus, menuPermitido]);

  useEffect(() => {
    const rotasPermitidas = menuPermitido.flatMap((section) => section.items.map((item) => item.key));
    if (!rotasPermitidas.includes(modo)) setModo(getPrimeiraRotaPermitida(menuPermitido));
  }, [modo, menuPermitido]);

  const handleOpenSpecificOS = useCallback((osId) => {
    setTargetOsId(osId); setSearchTerm(''); setIsSearchFocused(false); setModo('relatorios');
  }, []);

  const lojasDisponiveis = useMemo(() => {
    const lojasSet = new Set();
    empresasCadastradas.forEach((empresa) => { const label = getEmpresaLabel(empresa); if (label) lojasSet.add(label); });
    whatsappCanais.forEach((canal) => { const label = canal.codigoLoja || canal.raw?.codigoLoja || canal.raw?.loja || ''; if (label) lojasSet.add(String(label).trim()); });
    todasOS.forEach((os) => { lojasSet.add(normalizarLojaOS(os, empresasCadastradas)); });
    return ['Todas', ...Array.from(lojasSet).filter(Boolean).sort((a, b) => a.localeCompare(b, 'pt-BR'))];
  }, [todasOS, empresasCadastradas, whatsappCanais]);

  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return [];
    const term = searchTerm.toLowerCase();
    const cleanTerm = term.replace(/\D/g, '');
    return todasOS.filter((os) => {
      const nomeCliente = os.cliente?.nome?.toLowerCase() || '';
      const numOs = os.numeroOS ? String(os.numeroOS) : '';
      const whatsapp = os.whatsapp || os.cliente?.telefone || '';
      const cv = os.produto?.numeroCV?.toLowerCase() || '';
      return nomeCliente.includes(term) || numOs.includes(term) || cv.includes(term) || whatsapp.includes(term) || (cleanTerm && whatsapp.replace(/\D/g, '').includes(cleanTerm));
    }).slice(0, 5);
  }, [searchTerm, todasOS]);

  const fetchDashDados = useCallback(async () => {
    setIsGlobalLoading(true);
    try {
      const response = await api.get('/ordens-servico', { params: montarParamsEmpresa(empresaIdAtual) });
      setTodasOS(resolverArrayApi(response.data, ['ordens', 'ordensServico', 'registros']));
    } catch (error) {
      showToast('error', 'Falha ao carregar dados.');
    } finally {
      setIsGlobalLoading(false);
    }
  }, [showToast, empresaIdAtual]);

  const fetchMonthlySales = useCallback(async () => {
    try {
      const vendedorId = user?.id || user?._id || user?.usuarioId || '';
      let response = null;
      if (vendedorId) {
        try { response = await api.get('/api/vendedores/vendas-mes', { params: { ...montarParamsEmpresa(empresaIdAtual), vendedorId } }); } catch { response = null; }
      }
      if (!response && user?.nomeCompleto) {
        response = await api.get(`/api/vendedores/vendas-mes/${encodeURIComponent(user.nomeCompleto)}`, { params: montarParamsEmpresa(empresaIdAtual) });
      }
      if (response?.data?.success) setMonthlySales(response.data.totalVendido || 0);
      else setMonthlySales(0);
    } catch { setMonthlySales(0); }
  }, [user, empresaIdAtual]);

  const fetchAniversariantesHoje = useCallback(async () => {
    if (!empresaIdAtual) return;

    setLoadingAniversariantes(true);

    try {
      const hoje = new Date();
      const mesFiltro = `${String(hoje.getMonth() + 1).padStart(2, '0')}/${hoje.getFullYear()}`;

      const [resAniversariantes, resCampanhas] = await Promise.allSettled([
        api.get('/ordens-servico/api/campanhas/aniversariantes', {
          params: { empresaId: empresaIdAtual, mesFiltro, modo: 'HOJE' }
        }),
        api.get('/ordens-servico/api/campanhas', {
          params: { empresaId: empresaIdAtual }
        })
      ]);

      const aniversariantes = resAniversariantes.status === 'fulfilled'
        ? resolverArrayApi(resAniversariantes.value?.data, ['clientes', 'aniversariantes'])
        : [];

      const campanhas = resCampanhas.status === 'fulfilled'
        ? resolverArrayApi(resCampanhas.value?.data, ['campanhas'])
        : [];

      const hojeIso = toDateKey(new Date());
      const statusEnviado = new Set(['CONCLUIDA', 'CONCLUÍDA', 'ENVIADA', 'FINALIZADA', 'PROCESSANDO']);

      const enviadaHoje = campanhas.some((campanha) => {
        if (String(campanha?.tipoCampanha || '').toUpperCase() !== 'ANIVERSARIANTES') return false;
        if (String(campanha?.modoAniversario || '').toUpperCase() !== 'HOJE') return false;
        if (!statusEnviado.has(String(campanha?.statusCampanha || '').toUpperCase())) return false;

        const referencia = campanha?.enviadoEm || campanha?.updatedAt || campanha?.createdAt;
        if (!referencia) return true;

        return toDateKey(new Date(referencia)) === hojeIso;
      });

      setAniversariantesHoje(aniversariantes);
      setAniversariosEnviadosHoje(enviadaHoje);
    } catch (error) {
      console.error('Erro ao carregar aniversariantes:', error);
      setAniversariantesHoje([]);
      setAniversariosEnviadosHoje(false);
    } finally {
      setLoadingAniversariantes(false);
    }
  }, [empresaIdAtual]);

  const fetchAgendamentosDashboard = useCallback(async () => {
    const ids = new Set();

    if (empresaIdAtual) ids.add(String(empresaIdAtual));

    empresasDoUsuario.forEach((empresa) => {
      const id = getEmpresaObjectId(empresa);
      if (id) ids.add(String(id));
    });

    whatsappCanais.forEach((canal) => {
      const id = normalizarId(canal?.empresaId || canal?.unidadeId);
      if (id) ids.add(String(id));
    });

    if (!ids.size) {
      setAgendamentosDashboard([]);
      return;
    }

    setLoadingAgendamentosDashboard(true);
    setErroAgendamentosDashboard('');

    try {
      const respostas = await Promise.allSettled(
        Array.from(ids).map(async (empresaId) => {
          const response = await api.get('/api/agendamentos', { params: { empresaId } });
          return { empresaId, response };
        })
      );

      const mapa = new Map();
      let sucessos = 0;

      respostas.forEach((resultado) => {
        if (resultado.status !== 'fulfilled') return;
        sucessos += 1;

        const { empresaId, response } = resultado.value;
        const lista = resolverArrayApi(response?.data, ['agendamentos']);

        lista.forEach((item) => {
          const id = normalizarId(item?._id || item?.id) || `${empresaId}-${item?.data || ''}-${item?.hora || ''}-${item?.telefone || item?.nome || ''}`;
          mapa.set(id, {
            ...item,
            _id: item?._id || item?.id || id,
            id: item?.id || item?._id || id,
            empresaId: item?.empresaId || empresaId
          });
        });
      });

      setAgendamentosDashboard(
        Array.from(mapa.values()).sort((a, b) =>
          `${a?.data || ''} ${a?.hora || '99:99'}`.localeCompare(`${b?.data || ''} ${b?.hora || '99:99'}`)
        )
      );

      if (!sucessos) {
        setErroAgendamentosDashboard('Não foi possível carregar os agendamentos.');
      }
    } catch (error) {
      console.error('[Dashboard] Erro ao carregar agendamentos:', error);
      setAgendamentosDashboard([]);
      setErroAgendamentosDashboard('Não foi possível carregar os agendamentos.');
    } finally {
      setLoadingAgendamentosDashboard(false);
    }
  }, [empresaIdAtual, empresasDoUsuario, whatsappCanais]);

  const atualizarEstadoWhatsappPorCanais = useCallback((lista = []) => {
    const canaisNormalizados = lista.map((canal) => normalizarCanalWhatsapp(canal, empresaIdAtual));
    setWhatsappCanais(canaisNormalizados);
    const canalConectado = canaisNormalizados.find((canal) => canal.connected);
    const canalQr = canaisNormalizados.find((canal) => canal.status === WHATSAPP_STATUS.QRCODE && canal.qrCode);
    const canalLoading = canaisNormalizados.find((canal) => canal.status === WHATSAPP_STATUS.LOADING);
    if (canalConectado) {
      setWhatsappStatus(WHATSAPP_STATUS.CONNECTED); setQrCode(null); setShowQrModal(false);
      setWhatsappSession({ empresaId: canalConectado.empresaId || empresaIdAtual, sessionName: canalConectado.sessionName || '', canalWhatsappId: canalConectado.id || '', canalNome: canalConectado.nome || '' });
      return canaisNormalizados;
    }
    if (canalQr) {
      setWhatsappStatus(WHATSAPP_STATUS.QRCODE); setQrCode(null); setShowQrModal(false);
      setWhatsappSession({ empresaId: canalQr.empresaId || empresaIdAtual, sessionName: canalQr.sessionName || '', canalWhatsappId: canalQr.id || '', canalNome: canalQr.nome || '' });
      return canaisNormalizados;
    }
    if (canalLoading) { setWhatsappStatus(WHATSAPP_STATUS.LOADING); return canaisNormalizados; }
    setWhatsappStatus(WHATSAPP_STATUS.DISCONNECTED); setQrCode(null);
    return canaisNormalizados;
  }, [empresaIdAtual]);

  const fetchWhatsappCanais = useCallback(async () => {
    setIsLoadingWhatsappCanais(true);
    try {
      const response = await api.get('/api/whatsapp/canais');
      const lista = resolverArrayApi(response.data, ['canais', 'channels']);
      atualizarEstadoWhatsappPorCanais(lista);
      return lista;
    } catch { return null; } finally { setIsLoadingWhatsappCanais(false); }
  }, [atualizarEstadoWhatsappPorCanais]);

  const checkWhatsappStatus = useCallback(async () => {
    const canais = await fetchWhatsappCanais();
    if (Array.isArray(canais)) return canais.some((canal) => normalizarCanalWhatsapp(canal, empresaIdAtual).connected);
    setWhatsappStatus(WHATSAPP_STATUS.DISCONNECTED); setQrCode(null); return false;
  }, [fetchWhatsappCanais, empresaIdAtual]);

  const aplicarPayloadWhatsapp = useCallback((payload = {}) => {
    const canalIdPayload = normalizarId(payload.canalWhatsappId || payload.canalId);
    const empresaPayloadId = normalizarId(payload.empresaId);
    const matrizPayloadId = normalizarId(payload.empresaMatrizId || payload.empresaOrigemId);
    const grupoPayloadId = normalizarId(payload.grupoEmpresaId || payload.checkoutSessionId);
    const canalConhecido = canalIdPayload ? whatsappCanais.some((canal) => canal.id === canalIdPayload) : false;
    const pertenceAoGrupo = !empresaPayloadId || empresaPayloadId === empresaIdAtual || matrizPayloadId === empresaIdAtual || grupoPayloadId === empresaIdAtual || canalConhecido;
    if (!pertenceAoGrupo) return;

    const eventKey = [payload?.empresaId || empresaIdAtual || 'legacy', payload?.empresaMatrizId || '', payload?.grupoEmpresaId || '', payload?.canalWhatsappId || payload?.canalId || '', payload?.status || '', payload?.qrCode || payload?.qr || payload?.code || payload?.urlCode || '', payload?.timestamp || ''].join('|');
    if (eventKey && lastWhatsappEventRef.current === eventKey) return;
    lastWhatsappEventRef.current = eventKey;

    const canalId = canalIdPayload;
    const status = normalizarStatusBackend(payload.status, payload.connected);
    const qr = payload.qrCode || payload.qr || payload.code || payload.urlCode || null;

    if (canalId) {
      setWhatsappCanais((prev) => {
        const exists = prev.some((canal) => canal.id === canalId);
        if (!exists) return prev;
        return prev.map((canal) => {
          if (canal.id !== canalId) return canal;
          return { ...canal, status, connected: status === WHATSAPP_STATUS.CONNECTED, qrCode: status === WHATSAPP_STATUS.QRCODE ? qr || canal.qrCode : null, sessionName: payload.sessionName || canal.sessionName, lastStatusAt: payload.timestamp || new Date().toISOString() };
        });
      });
    }

    setWhatsappSession({ empresaId: payload?.empresaId ? String(payload.empresaId) : empresaIdAtual, sessionName: payload?.sessionName || whatsappSession.sessionName || '', canalWhatsappId: canalId || whatsappSession.canalWhatsappId || '', canalNome: payload?.canalNome || whatsappSession.canalNome || '' });

    if (status === WHATSAPP_STATUS.QRCODE || qr) { setWhatsappStatus(WHATSAPP_STATUS.QRCODE); setQrCode(null); setShowQrModal(false); return; }
    if (status === WHATSAPP_STATUS.CONNECTED) { setWhatsappStatus(WHATSAPP_STATUS.CONNECTED); setQrCode(null); setShowQrModal(false); showToast('success', 'WhatsApp conectado com sucesso!'); return; }
    if (status === WHATSAPP_STATUS.DISCONNECTING) { setWhatsappStatus(WHATSAPP_STATUS.DISCONNECTING); setQrCode(null); return; }
    if (status === WHATSAPP_STATUS.LOADING) { setWhatsappStatus(WHATSAPP_STATUS.LOADING); setShowQrModal(false); setQrCode(null); return; }
    setWhatsappStatus(WHATSAPP_STATUS.DISCONNECTED); setQrCode(null);
  }, [empresaIdAtual, whatsappSession, showToast, whatsappCanais]);

  const abrirSeletorCanaisWhatsapp = async () => {
    setShowProfileMenu(false); setShowQrModal(false); setQrCode(null);
    const lista = await fetchWhatsappCanais();
    const canaisAtualizados = Array.isArray(lista) ? lista.map((canal) => normalizarCanalWhatsapp(canal, empresaIdAtual)) : whatsappCanais;
    if (!canaisAtualizados.length) { showToast('error', 'Nenhum canal de WhatsApp cadastrado. Crie um canal de comunicação para a empresa.'); navigate('whatsapp'); return; }
    setShowWhatsappChannelModal(true);
  };

  const connectWhatsapp = async (canalEscolhido = null) => {
    const canalAlvo = canalEscolhido?.id ? canalEscolhido : canalPrincipal;
    if (!canalAlvo?.id) { showToast('error', 'Selecione um canal de WhatsApp antes de conectar.'); setShowProfileMenu(false); setShowWhatsappChannelModal(false); abrirCentralWhatsapp(); return; }
    setWhatsappCanalEmAcaoId(canalAlvo.id);
    try { abrirCentralWhatsapp(canalAlvo); } finally { setWhatsappCanalEmAcaoId(''); }
  };

  const abrirCentralWhatsapp = (canal = null) => {
    setShowProfileMenu(false); setShowWhatsappChannelModal(false); setShowQrModal(false); setQrCode(null);
    const params = new URLSearchParams();
    params.set('abrirCanal', '1');
    if (canal?.id) params.set('canalWhatsappId', canal.id);
    if (canal?.empresaId) params.set('empresaId', canal.empresaId);
    if (canal?.unidadeId) params.set('unidadeId', canal.unidadeId);
    if (canal?.empresaMatrizId) params.set('empresaMatrizId', canal.empresaMatrizId);
    window.location.href = `/empresas?${params.toString()}`;
  };

  const handleLogout = useCallback(() => {
    try {
      localStorage.removeItem('vizumToken'); localStorage.removeItem('vizumUser'); localStorage.removeItem('vizumEmpresa'); localStorage.removeItem('authToken'); localStorage.removeItem('token'); sessionStorage.removeItem('lumix_login_email');
    } catch { /* ignore */ }
    window.location.href = '/login';
  }, []);

  const handleSearchSubmit = (e) => {
    if (e.key === 'Enter') { navigate('lista'); setIsSearchFocused(false); }
  };

  const startVoiceSearch = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.lang = 'pt-BR';
      recognition.onstart = () => { setIsListening(true); showToast('success', 'Ouvindo...'); };
      recognition.onend = () => setIsListening(false);
      recognition.onresult = (e) => {
        const transcript = e.results[0][0].transcript;
        setSearchTerm(transcript); setIsSearchFocused(true); searchInputRef.current?.focus();
      };
      recognition.start();
    } else { showToast('error', 'Pesquisa por voz não suportada.'); }
  };

  useEffect(() => { fetchDashDados(); }, [fetchDashDados]);
  useEffect(() => { fetchAniversariantesHoje(); }, [fetchAniversariantesHoje]);
  useEffect(() => { fetchAgendamentosDashboard(); }, [fetchAgendamentosDashboard]);
  useEffect(() => { checkWhatsappStatus(); }, [checkWhatsappStatus]);

  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken') || localStorage.getItem('vizumToken') || '';
    const socket = io(socketUrl, {
      transports: ['websocket', 'polling'], auth: token ? { token } : undefined, query: empresaIdAtual ? { empresaId: empresaIdAtual } : undefined, reconnection: true, reconnectionAttempts: 8, reconnectionDelay: 800
    });
    const handleWhatsappStatus = (data) => aplicarPayloadWhatsapp(data);
    const handleLegacyStatus = (payload) => {
      const data = typeof payload === 'object' ? payload : { status: payload, empresaId: empresaIdAtual };
      aplicarPayloadWhatsapp(data);
    };
    const handleLegacyQr = (qr) => {
      const qrString = typeof qr === 'object' ? qr.code || qr.qrCode || qr.qr : qr;
      aplicarPayloadWhatsapp({ empresaId: empresaIdAtual, status: 'QRCODE', qrCode: qrString });
    };
    socket.on('whatsapp_status', handleWhatsappStatus);
    socket.on('status', handleLegacyStatus);
    socket.on('qrCode', handleLegacyQr);
    if (empresaIdAtual) { socket.on(`whatsapp_status_${empresaIdAtual}`, handleWhatsappStatus); socket.on(`whatsapp_status:${empresaIdAtual}`, handleWhatsappStatus); }
    return () => {
      socket.off('whatsapp_status', handleWhatsappStatus); socket.off('status', handleLegacyStatus); socket.off('qrCode', handleLegacyQr);
      if (empresaIdAtual) { socket.off(`whatsapp_status_${empresaIdAtual}`, handleWhatsappStatus); socket.off(`whatsapp_status:${empresaIdAtual}`, handleWhatsappStatus); }
      socket.disconnect();
    };
  }, [socketUrl, empresaIdAtual, aplicarPayloadWhatsapp]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); searchInputRef.current?.focus(); }
      if (e.key === 'Escape') { closeMenus(); setShowQrModal(false); setShowWhatsappChannelModal(false); }
    };
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) setIsSearchFocused(false);
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) setShowProfileMenu(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => { window.removeEventListener('keydown', handleKeyDown); document.removeEventListener('mousedown', handleClickOutside); };
  }, [closeMenus]);

  const dashboardKpis = useMemo(() => {
    const hojeStr = new Date().toISOString().split('T')[0];
    const mesAtualStr = hojeStr.substring(0, 7);
    const limiteAlerta = new Date();
    limiteAlerta.setDate(limiteAlerta.getDate() + diasFiltro);

    let kpiLab = 0; let totalPedidoLente = 0; let totalLaboratorio = 0; let totalEntregues = 0; let totalProntoLoja = 0; let totalAtrasados = 0; let totalPrevistoHoje = 0; let totalOutrosStatus = 0; let valorHoje = 0; let valorMes = 0; let qtdeVendasMes = 0;
    const proximas = []; const vendedoresMap = {}; const diasNoMes = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
    const vendasDiariasMap = Array.from({ length: diasNoMes }, (_, i) => ({ dia: `D${i + 1}`, valor: 0 }));

    todasOS.filter((os) => {
      const lojaNormalizada = normalizarLojaOS(os, empresasCadastradas);
      return lojaAtiva === 'Todas' || lojaNormalizada === lojaAtiva;
    }).forEach((os) => {
      const valor = Number(os.valorTotal || os.pagamento?.valorTotal || os.sinal || 0);
      const statusOperacional = classificarStatusOperacional(os);
      if (statusOperacional.key === 'pedidoLente') totalPedidoLente++;
      if (statusOperacional.key === 'laboratorio') totalLaboratorio++;
      if (statusOperacional.key === 'entregue') totalEntregues++;
      if (statusOperacional.key === 'prontoLoja') totalProntoLoja++;
      if (statusOperacional.key === 'outros') totalOutrosStatus++;
      kpiLab = totalPedidoLente + totalLaboratorio;
      const entrega = os.dataEntrega ? new Date(os.dataEntrega) : null;
      const hojeInicio = new Date(); hojeInicio.setHours(0, 0, 0, 0);
      const statusFinalizado = statusOperacional.key === 'entregue';

      if (entrega && !Number.isNaN(entrega.getTime()) && !statusFinalizado) {
        if (entrega < hojeInicio) totalAtrasados++;
        if (isMesmoDia(entrega)) totalPrevistoHoje++;
      }
      if (os.dataCompra) {
        if (String(os.dataCompra).startsWith(hojeStr)) valorHoje += valor;
        if (String(os.dataCompra).startsWith(mesAtualStr)) {
          qtdeVendasMes++; valorMes += valor;
          const vendedor = resolverNomeVendedorOS(os);
          vendedoresMap[vendedor] = (vendedoresMap[vendedor] || 0) + valor;
          const diaVenda = parseInt(String(os.dataCompra).split('-')[2], 10);
          if (diaVenda && vendasDiariasMap[diaVenda - 1]) vendasDiariasMap[diaVenda - 1].valor += valor;
        }
      }
      if (os.dataEntrega && os.status !== 'Entregue') {
        const d = new Date(os.dataEntrega);
        const ontem = new Date(); ontem.setDate(ontem.getDate() - 1);
        if (d <= limiteAlerta && d >= ontem) proximas.push(os);
      }
    });

    return {
      kpiLab, totalEntregues, totalProntoLoja, valorHoje, valorMes, ticketMedio: qtdeVendasMes > 0 ? valorMes / qtdeVendasMes : 0,
      rankingVendedores: Object.entries(vendedoresMap).map(([name, valor]) => ({ name, valor })).sort((a, b) => b.valor - a.valor).slice(0, 5),
      totalPedidoLente, totalLaboratorio, totalAtrasados, totalPrevistoHoje, totalOutrosStatus,
      dadosStatus: [
        { name: 'Pedido de lente', value: totalPedidoLente, color: CHART_COLORS.primary, helper: 'Compras/solicitações de lente' },
        { name: 'No laboratório', value: totalLaboratorio, color: CHART_COLORS.warning, helper: 'Em produção técnica' },
        { name: 'Pronto na loja', value: totalProntoLoja, color: CHART_COLORS.info, helper: 'Aguardando retirada' },
        { name: 'Entregues', value: totalEntregues, color: CHART_COLORS.success, helper: 'Finalizadas' },
        { name: 'Atrasadas', value: totalAtrasados, color: CHART_COLORS.danger, helper: 'Entrega passou do prazo' },
        { name: 'Entrega hoje', value: totalPrevistoHoje, color: '#8B5CF6', helper: 'Previsão para hoje' },
        { name: 'Outros', value: totalOutrosStatus, color: CHART_COLORS.neutral, helper: 'Sem categoria definida' }
      ].filter((s) => s.value > 0),
      vendasDiariasMap,
      entregasProximas: proximas.sort((a, b) => new Date(a.dataEntrega) - new Date(b.dataEntrega))
    };
  }, [todasOS, diasFiltro, lojaAtiva, empresasCadastradas]);


  const dashboardEventos = useMemo(() => {
    const hojeKey = toDateKey(new Date());
    const osFiltradas = todasOS.filter((os) => {
      const lojaNormalizada = normalizarLojaOS(os, empresasCadastradas);
      return lojaAtiva === 'Todas' || lojaNormalizada === lojaAtiva;
    });

    const entregas = osFiltradas
      .filter((os) => {
        if (!os.dataEntrega) return false;
        const statusOperacional = classificarStatusOperacional(os);
        return statusOperacional.key !== 'entregue';
      })
      .map((os) => {
        const dataKey = toDateKey(os.dataEntrega);
        const atrasado = dataKey && dataKey < hojeKey;
        const hoje = dataKey === hojeKey;
        return {
          id: os._id || os.id || `os-${os.numeroOS}`,
          tipo: atrasado ? 'atrasada' : hoje ? 'entrega-hoje' : 'entrega',
          data: dataKey,
          hora: os.horaEntrega || os.hora || '',
          titulo: atrasado ? 'Entrega atrasada' : hoje ? 'Entrega prevista hoje' : 'Entrega prevista',
          cliente: getOSClienteNome(os),
          helper: `OS #${os.numeroOS || '---'} · ${normalizarLojaOS(os, empresasCadastradas)}`,
          loja: normalizarLojaOS(os, empresasCadastradas),
          primeiraFoto: getOSPrimeiraFoto(os),
          lente: getOSLenteResumo(os),
          whatsapp: getOSWhatsappResumo(os),
          dataCompra: formatDateLocal(os.dataCompra || os.createdAt),
          valor: getOSValorResumo(os),
          tone: atrasado ? 'red' : hoje ? 'purple' : 'green',
          os
        };
      })
      .filter((evento) => evento.data);

    const compromissos = agendamentosDashboard
      .filter((agendamento) => String(agendamento?.status || '').toLowerCase() !== 'cancelado')
      .filter((agendamento) => {
        const loja = normalizarLojaAgendamento(agendamento, empresasCadastradas);
        return lojaAtiva === 'Todas' || loja === lojaAtiva;
      })
      .map((agendamento) => {
        const loja = normalizarLojaAgendamento(agendamento, empresasCadastradas);
        const statusLabel = getAgendamentoStatusLabel(agendamento?.status);
        return {
          id: normalizarId(agendamento?._id || agendamento?.id) || `agenda-${agendamento?.data}-${agendamento?.hora}-${agendamento?.telefone || agendamento?.nome}`,
          tipo: 'compromisso',
          data: String(agendamento?.data || '').slice(0, 10),
          hora: String(agendamento?.hora || ''),
          titulo: getAgendamentoTipoLabel(agendamento?.tipo),
          cliente: String(agendamento?.nome || 'Cliente'),
          helper: `${statusLabel} · ${loja}`,
          loja,
          whatsapp: String(agendamento?.telefone || ''),
          tone: getAgendamentoTone(agendamento?.status),
          agendamento
        };
      })
      .filter((evento) => evento.data);

    return [...entregas, ...compromissos].sort((a, b) => `${a.data} ${a.hora || '99:99'}`.localeCompare(`${b.data} ${b.hora || '99:99'}`));
  }, [todasOS, agendamentosDashboard, lojaAtiva, empresasCadastradas]);

  const dashboardDiasCalendario = useMemo(() => getCalendarDays(dashboardCalendarDate), [dashboardCalendarDate]);
  const dashboardEventosPorDia = useMemo(() => {
    const mapa = {};
    dashboardEventos.forEach((evento) => {
      if (!mapa[evento.data]) mapa[evento.data] = [];
      mapa[evento.data].push(evento);
    });
    return mapa;
  }, [dashboardEventos]);

  const dashboardEventosSelecionados = dashboardEventosPorDia[dashboardSelectedDate] || [];
  const dashboardAtrasados = dashboardEventos.filter((evento) => evento.tipo === 'atrasada');
  const dashboardEntregasHoje = dashboardEventos.filter((evento) => evento.tipo === 'entrega-hoje');
  const dashboardCompromissos = dashboardEventos.filter((evento) => evento.tipo === 'compromisso' || evento.tipo === 'entrega-agendada');
  const dashboardEntregasHojeComOS = dashboardEntregasHoje.filter((evento) => evento.os);
  const dashboardEntregasSelecionadas = dashboardEventosSelecionados.filter((evento) => evento.os);
  const hojeDashboardKey = toDateKey(new Date());
  const dashboardAgendamentosHoje = dashboardCompromissos
    .filter((evento) => evento.data === hojeDashboardKey)
    .sort((a, b) => String(a.hora || '99:99').localeCompare(String(b.hora || '99:99')));
  const horaAgora = `${String(new Date().getHours()).padStart(2, '0')}:${String(new Date().getMinutes()).padStart(2, '0')}`;
  const proximoAgendamentoHoje = dashboardAgendamentosHoje.find((evento) => !evento.hora || evento.hora >= horaAgora) || dashboardAgendamentosHoje[0] || null;
  const totalAniversariantesHoje = aniversariantesHoje.length;
  const aniversariantesPendentesHoje = aniversariosEnviadosHoje ? 0 : totalAniversariantesHoje;
  const aniversariantesPreview = aniversariantesHoje.slice(0, 4);
  const dashboardCalendarTitle = `${DASH_MONTHS[dashboardCalendarDate.getMonth()]} ${dashboardCalendarDate.getFullYear()}`;

  const mudarMesDashboard = (offset) => {
    setDashboardCalendarDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + offset, 1, 12));
  };

  const irMesAtualDashboard = () => {
    const hoje = new Date();
    setDashboardCalendarDate(new Date(hoje.getFullYear(), hoje.getMonth(), 1, 12));
    setDashboardSelectedDate(toDateKey(hoje));
  };

  const saudacao = new Date().getHours() < 12 ? 'Bom dia' : new Date().getHours() < 18 ? 'Boa tarde' : 'Boa noite';

  return (
    <div className={`vizum-app-shell ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <GlobalStyles />
      
      {splashStage !== 'hidden' && (
        <div className={`splash-overlay ${splashStage === 'fading' ? 'fade-out' : ''}`}>
          <div className="simple-splash-card">
            <img src={logoIconPng} alt="Lumix Óticas" className="splash-logo-img" />
            <span className="splash-loader-line" />
          </div>
          <div className="splash-text">
            <h2>Olá, {userName}.</h2>
            <p key={splashMessageIndex} className="fade-in">{SPLASH_MESSAGES[splashMessageIndex]}</p>
          </div>
        </div>
      )}

      <div className={`mobile-sidebar-overlay ${isSidebarOpen ? 'active' : ''}`} onClick={() => setSidebarOpen(false)} />

      <aside className={`app-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="brand-header">
          <button className="icon-btn mobile-only" onClick={() => setSidebarOpen(false)} type="button" title="Fechar menu"><Icons.Close /></button>
          <div className="brand-logo">
            <div className="brand-logo-mark">
              <img src={logoIconPng} alt="Lumix" className="logo-icon-invert" />
            </div>
            <span className="brand-text">
              <span className="brand-lumix">Lumix</span>{' '}
              <span className="brand-opticas">
                <span className="brand-o">Ó</span>
                <span className="brand-t">t</span>
                <span className="brand-i">i</span>
                <span className="brand-c">c</span>
                <span className="brand-a">a</span>
                <span className="brand-s">s</span>
              </span>
            </span>
          </div>
        </div>

        <nav className="nav-menu custom-scrollbar">
          {menuPermitido.map((section) => (
            <div className={`nav-section nav-section-${section.key}`} key={section.key}>
              {!isSidebarCollapsed && (
                <div className="nav-section-header">
                  <span className="nav-section-title">{section.label}</span>
                </div>
              )}
              <div className="nav-section-items">
                {section.items.map((item) => (
                  <NavItem key={item.key} icon={item.icon} label={item.label} active={modo === item.key} collapsed={isSidebarCollapsed} destaque={item.destaque} onClick={() => navigate(item.key)} />
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button
            type="button"
            className={`nav-item btn-sidebar-logout ${isSidebarCollapsed ? 'collapsed' : ''}`}
            onClick={handleLogout}
            title={isSidebarCollapsed ? 'Sair da conta' : ''}
          >
            <div className="nav-icon"><Icons.LogOut /></div>
            {!isSidebarCollapsed && <span className="nav-label">Sair da conta</span>}
          </button>
        </div>
      </aside>

      <main className="app-main">
        <header className="app-topbar">
          <div className="topbar-left">
            <button className="icon-btn desktop-only" onClick={() => setSidebarCollapsed(!isSidebarCollapsed)} type="button" title={isSidebarCollapsed ? 'Abrir menu' : 'Recolher menu'}><Icons.Menu /></button>
            <button className="icon-btn mobile-only" onClick={() => setSidebarOpen(true)} type="button" title="Abrir menu"><Icons.Menu /></button>

            <a href="/home" className="back-home-btn" title="Voltar para a Home">
              <Icons.Dashboard />
              <span>Voltar para Home</span>
            </a>

            <div className="route-context">
              <span>{rotaAtual.sectionLabel}</span>
              <strong>{rotaAtual.label}</strong>
            </div>
          </div>
          <div className="topbar-center" ref={searchContainerRef}>
            <div className={`search-omnibox ${isSearchFocused ? 'focused' : ''}`}>
              <Icons.Search />
              <input ref={searchInputRef} type="text" placeholder="Pesquisar cliente" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onFocus={() => setIsSearchFocused(true)} onKeyDown={handleSearchSubmit} />
              <button className={`icon-btn-mic ${isListening ? 'listening' : ''}`} onClick={startVoiceSearch} title="Pesquisar por voz" type="button"><Icons.Mic /></button>
            </div>
            {isSearchFocused && searchTerm.trim() && (
              <div className="search-dropdown slide-down">
                {searchResults.length > 0 ? (
                  <ul className="search-results-list">
                    {searchResults.map((os) => (
                      <li key={os._id} className="search-result-item" onClick={() => handleOpenSpecificOS(os._id)}>
                        <div className="result-icon"><Icons.Box /></div>
                        <div className="result-info">
                          <span className="result-name">{os.cliente?.nome || 'Sem nome'}</span>
                          <span className="result-sub">OS #{os.numeroOS || '---'} • 📞 {os.whatsapp || os.cliente?.telefone || '---'}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : <div className="search-empty">Nenhum resultado encontrado.</div>}
              </div>
            )}
          </div>
          <div className="topbar-right">
            <div className="user-profile-wrapper" ref={profileMenuRef}>
              <button className="avatar-btn" onClick={() => { setShowProfileMenu((prev) => !prev); if (monthlySales === null) fetchMonthlySales(); fetchWhatsappCanais(); }} type="button">
                {userInitials}
                <span className={`status-dot ${whatsappStatus === WHATSAPP_STATUS.CONNECTED ? 'online' : 'offline'}`} />
              </button>
              {showProfileMenu && (
                <div className="profile-menu professional-profile-menu slide-down">
                  <div className="profile-menu-head">
                    <div className="profile-large-avatar">{userInitials}</div>
                    <div className="profile-account-copy">
                      <strong>{user.nomeCompleto || user.nome || 'Usuário'}</strong>
                      <span>{user.email || 'conta@lumixoticas.com.br'}</span>
                      <small>{tipoUsuario === 'admin' ? 'Administrador' : 'Vendedor'} · {lojaAtiva === 'Todas' ? 'Todas as lojas' : lojaAtiva}</small>
                    </div>
                  </div>

                  <div className="profile-menu-section">
                    <button
                      type="button"
                      className={`profile-wa-row ${whatsappStatus === WHATSAPP_STATUS.CONNECTED ? 'connected' : ''}`}
                      onClick={abrirSeletorCanaisWhatsapp}
                      disabled={whatsappStatus === WHATSAPP_STATUS.LOADING || whatsappStatus === WHATSAPP_STATUS.DISCONNECTING || isLoadingWhatsappCanais}
                    >
                      <span className="profile-wa-icon">{whatsappStatus === WHATSAPP_STATUS.QRCODE ? <Icons.Qr /> : <Icons.Message />}</span>
                      <span className="profile-wa-copy">
                        <strong>WhatsApp da empresa</strong>
                        <small>{whatsappStatusTexto}</small>
                        <em>{canalPrincipal ? `${canalPrincipal.unidadeNome} · ${canalPrincipal.nome}` : whatsappSession?.sessionName || 'Nenhum canal principal'}</em>
                      </span>
                      <span className="profile-wa-action">{getWhatsappButtonLabel(whatsappStatus)}</span>
                    </button>
                  </div>

                  <div className="profile-menu-actions">
                    <button type="button" onClick={() => navigate('whatsapp')}><Icons.Message /> Abrir atendimento</button>
                    <button type="button" className="danger" onClick={handleLogout}><Icons.LogOut /> Sair da conta</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        <section className="content-scroll custom-scrollbar">
          {modo === 'home' && (
            <div className="clean-dashboard ops-dashboard fade-in">
              <header className="dashboard-welcome">
                <h1>Bem-vindo de volta, {userName}.</h1>

                <div className="dashboard-highlight-row">
                  <button
                    type="button"
                    className={`birthday-dashboard-alert ${aniversariantesPendentesHoje > 0 ? 'pending' : 'done'}`}
                    onClick={() => navigate('templates')}
                  >
                    <span className="birthday-dashboard-icon"><Icons.Birthday /></span>

                    <span className="birthday-dashboard-copy">
                      <span className="dashboard-widget-kicker">Aniversários</span>
                      <strong>
                        {loadingAniversariantes
                          ? 'Verificando clientes...'
                          : totalAniversariantesHoje > 0
                            ? `${totalAniversariantesHoje} aniversariante${totalAniversariantesHoje === 1 ? '' : 's'} hoje`
                            : 'Nenhum aniversariante hoje'}
                      </strong>
                      <small>
                        {loadingAniversariantes
                          ? 'Atualizando a base'
                          : aniversariantesPendentesHoje > 0
                            ? `${aniversariantesPendentesHoje} mensagem${aniversariantesPendentesHoje === 1 ? '' : 'ens'} aguardando envio`
                            : totalAniversariantesHoje > 0
                              ? 'Campanha de hoje já processada'
                              : 'Sem ações pendentes'}
                      </small>
                    </span>

                    <span className="birthday-dashboard-avatars" aria-hidden="true">
                      {aniversariantesPreview.map((cliente, index) => (
                        <i key={`birthday-${cliente?._id || cliente?.id || index}`}>{getInitials(getAniversarianteNome(cliente))}</i>
                      ))}
                      {totalAniversariantesHoje > aniversariantesPreview.length && (
                        <i className="more">+{totalAniversariantesHoje - aniversariantesPreview.length}</i>
                      )}
                    </span>

                    <span className="birthday-dashboard-action">
                      {aniversariantesPendentesHoje > 0 ? 'Enviar' : 'Abrir'}
                    </span>
                  </button>

                  <button
                    type="button"
                    className={`agenda-dashboard-widget ${dashboardAgendamentosHoje.length ? 'has-items' : ''}`}
                    onClick={() => { window.location.href = '/agendamentos'; }}
                  >
                    <span className="agenda-dashboard-icon"><Icons.Calendar /></span>
                    <span className="agenda-dashboard-copy">
                      <span className="dashboard-widget-kicker">Agendamentos</span>
                      <strong>
                        {loadingAgendamentosDashboard
                          ? 'Carregando agenda...'
                          : `${dashboardAgendamentosHoje.length} compromisso${dashboardAgendamentosHoje.length === 1 ? '' : 's'} hoje`}
                      </strong>
                      <small>
                        {erroAgendamentosDashboard
                          ? erroAgendamentosDashboard
                          : proximoAgendamentoHoje
                            ? `Próximo: ${proximoAgendamentoHoje.hora || 'dia'} · ${proximoAgendamentoHoje.cliente}`
                            : 'Nenhum compromisso para hoje'}
                      </small>
                    </span>
                    <span className="agenda-dashboard-list">
                      {dashboardAgendamentosHoje.slice(0, 3).map((evento) => (
                        <i key={`agenda-widget-${evento.id}`}>
                          <b>{evento.hora || '--:--'}</b>
                          <em>{String(evento.cliente || 'Cliente').split(' ')[0]}</em>
                        </i>
                      ))}
                    </span>
                    <span className="agenda-dashboard-action">Abrir</span>
                  </button>

                  <section className="today-deliveries-highlight">
                    <div className="today-deliveries-head">
                      <span className="today-deliveries-icon"><Icons.Calendar /></span>
                      <span>
                        <strong>Óculos para entregar hoje</strong>
                        <small>
                          {dashboardEntregasHojeComOS.length
                            ? `${dashboardEntregasHojeComOS.length} entrega${dashboardEntregasHojeComOS.length === 1 ? '' : 's'} em ${lojaAtiva === 'Todas' ? 'todas as lojas' : lojaAtiva}`
                            : `Nenhuma entrega em ${lojaAtiva === 'Todas' ? 'todas as lojas' : lojaAtiva}`}
                        </small>
                      </span>
                    </div>

                    <div className="today-delivery-clients">
                      {dashboardEntregasHojeComOS.length ? (
                        dashboardEntregasHojeComOS.slice(0, 7).map((evento) => (
                          <button
                            type="button"
                            className="today-delivery-client"
                            key={`today-delivery-${evento.id}`}
                            onClick={() => handleOpenSpecificOS(evento.os?._id || evento.os?.id)}
                            title={`Abrir OS de ${evento.cliente}`}
                          >
                            <DeliveryClientAvatar os={evento.os} />
                            <span>{String(evento.cliente || 'Cliente').split(' ')[0]}</span>
                          </button>
                        ))
                      ) : (
                        <div className="today-deliveries-empty">
                          <span>Agenda livre</span>
                        </div>
                      )}

                      {dashboardEntregasHojeComOS.length > 7 ? (
                        <button
                          type="button"
                          className="today-delivery-more"
                          onClick={() => setDashboardSelectedDate(toDateKey(new Date()))}
                        >
                          +{dashboardEntregasHojeComOS.length - 7}
                        </button>
                      ) : null}
                    </div>
                  </section>
                </div>
              </header>

              <section className="ops-calendar-layout">
                <div className="clean-card ops-calendar-card">
                  <div className="clean-card-header ops-calendar-top">
                    <div>
                      <h3>Calendário de Fluxo</h3>
                      <span>Entregas e compromissos</span>
                    </div>
                    <div className="ops-calendar-actions">
                      <label className="calendar-store-filter">
                        <Icons.Store />
                        <select value={lojaAtiva} onChange={(event) => setLojaAtiva(event.target.value)}>
                          {lojasDisponiveis.map((loja) => (
                            <option value={loja} key={loja}>
                              {loja === 'Todas' ? 'Todas as lojas' : loja}
                            </option>
                          ))}
                        </select>
                      </label>
                      <button className="clean-link-btn current-month-btn" onClick={irMesAtualDashboard} type="button">Mês atual</button>
                      <button className="ops-month-btn" onClick={() => mudarMesDashboard(-1)} type="button"><Icons.ChevronDown /></button>
                      <strong>{dashboardCalendarTitle}</strong>
                      <button className="ops-month-btn next" onClick={() => mudarMesDashboard(1)} type="button"><Icons.ChevronDown /></button>
                    </div>
                  </div>

                  <div className="ops-calendar-weekdays">
                    {DASH_WEEKDAYS.map((dia) => <span key={dia}>{dia}</span>)}
                  </div>

                  <div className="ops-calendar-grid">
                    {dashboardDiasCalendario.map((dia) => {
                      const eventos = dia.empty ? [] : (dashboardEventosPorDia[dia.dateKey] || []);
                      const hasAtraso = eventos.some((evento) => evento.tipo === 'atrasada');
                      const hasEntrega = eventos.some((evento) => evento.tipo === 'entrega' || evento.tipo === 'entrega-hoje' || evento.tipo === 'entrega-agendada');
                      const hasAgenda = eventos.some((evento) => evento.tipo === 'compromisso');
                      const isSelected = dashboardSelectedDate === dia.dateKey;
                      const isToday = toDateKey(new Date()) === dia.dateKey;
                      return (
                        <button
                          key={dia.key}
                          type="button"
                          disabled={dia.empty}
                          className={`ops-day-cell ${dia.empty ? 'empty' : ''} ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${hasAtraso ? 'has-red' : ''} ${hasEntrega ? 'has-green' : ''} ${hasAgenda ? 'has-blue' : ''}`}
                          onClick={() => !dia.empty && setDashboardSelectedDate(dia.dateKey)}
                        >
                          {!dia.empty && (
                            <>
                              <span className="ops-day-number">{dia.dia}</span>
                              {!!eventos.length && <strong className="event-count">{eventos.length}</strong>}
                              <span className="ops-day-dots">
                                {hasAtraso && <i className="dot-red" />}
                                {hasEntrega && <i className="dot-green" />}
                                {hasAgenda && <i className="dot-blue" />}
                              </span>
                            </>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <aside className="clean-card ops-day-panel">
                  <div className="clean-card-header">
                    <div>
                      <h3>{formatDashboardDate(dashboardSelectedDate)}</h3>
                      <span>Detalhes do dia selecionado</span>
                    </div>
                    <strong className="clean-count-badge">{dashboardEventosSelecionados.length} Itens</strong>
                  </div>

                  <div className="ops-event-list custom-scrollbar">
                    {dashboardEventosSelecionados.length > 0 ? (
                      dashboardEventosSelecionados.map((evento) => (
                        <button
                          className={`ops-event-item tone-${evento.tone}`}
                          key={`${evento.tipo}-${evento.id}-${evento.data}`}
                          type="button"
                          onClick={() => evento.os ? handleOpenSpecificOS(evento.os._id || evento.os.id) : (window.location.href = '/agendamentos')}
                        >
                          <div className="event-time-col">
                            <span className="ops-event-time">{evento.hora || 'Dia'}</span>
                            <div className="event-line-connector"></div>
                          </div>
                          {evento.os ? (
                            <DeliveryClientAvatar os={evento.os} className="calendar-event-avatar" />
                          ) : null}
                          <span className="ops-event-copy">
                            <strong>{evento.titulo}</strong>
                            <small>{evento.cliente}</small>
                            <em>{evento.helper}</em>
                            {evento.os && (
                              <span className="ops-event-details">
                                <i><Icons.Box /> {evento.lente}</i>
                                <i><Icons.Message /> {evento.whatsapp || 'Sem número'}</i>
                              </span>
                            )}
                          </span>
                        </button>
                      ))
                    ) : (
                      <div className="clean-empty-state">
                        <Icons.Calendar />
                        <strong>Dia livre</strong>
                        <p>Nenhuma entrega ou compromisso agendado para esta data.</p>
                        <button className="clean-secondary-btn" onClick={() => navigate('nova')} type="button"><Icons.Plus /> Criar venda</button>
                      </div>
                    )}
                  </div>
                </aside>
              </section>
            </div>
          )}

          {modo !== 'home' && (
            <ModuleWorkspace>
              {modo === 'nova' && <OSForm onCancel={() => navigate('home')} onSuccess={() => { navigate('lista'); fetchDashDados(); }} />}
              {modo === 'lista' && <OSList />}
              {modo === 'simulador' && <SimuladorUltraRealista />}
              {modo === 'robo' && <RoboPosVenda />}
              {modo === 'config-vendas' && <RoboConfig />}
              {modo === 'agentes' && <AgentesRobos showToast={showToast} />}
              {modo === 'templates' && <TemplatesManager />}
              {modo === 'whatsapp' && <WhatsAppPro empresaIdAtual={empresaIdAtual} canaisWhatsapp={whatsappCanais} canalPrincipal={canalPrincipal} />}
              {modo === 'relatorios' && <OSReports initialOsId={targetOsId} />}
              {modo === 'insights' && <InsightsDashboard />}
              {modo === 'caixa' && <FluxoDeCaixa onOpenOS={handleOpenSpecificOS} />}
              {modo === 'dre' && <DREOtica />}
              {modo === 'estoque' && <Estoque />}
              {modo === 'backup' && <BackupFotos />}
            </ModuleWorkspace>
          )}
        </section>
      </main>

      {isListening && (
        <div className="voice-search-overlay fade-in">
          <div className="voice-search-card slide-up">
            <div className="voice-pulse-ring ring-one" />
            <div className="voice-pulse-ring ring-two" />
            <div className="voice-big-mic"><Icons.Mic /></div>
            <h3>Ouvindo...</h3>
            <p>Fale o nome do cliente ou número da OS</p>
            <div className="voice-wave"><span /><span /><span /><span /><span /></div>
          </div>
        </div>
      )}

      {showWhatsappChannelModal && (
        <WhatsappChannelSelectorModal canais={whatsappCanais} loading={isLoadingWhatsappCanais} canalEmAcaoId={whatsappCanalEmAcaoId} onClose={() => setShowWhatsappChannelModal(false)} onConnect={connectWhatsapp} onManage={abrirCentralWhatsapp} />
      )}

      {showQrModal && (
        <div className="modal-backdrop fade-in">
          <div className="modal-box qr-modal slide-up">
            <div className="modal-top">
              <div className="modal-titles">
                <h3>Conectar WhatsApp</h3>
                <p>Abra o WhatsApp no celular, vá em Aparelhos conectados e escaneie o QR Code.</p>
              </div>
              <button onClick={() => setShowQrModal(false)} className="icon-btn-close" type="button"><Icons.Close /></button>
            </div>
            <div className="qr-container">
              {qrCode ? (
                <div className="qr-code-fade-in fade-in">
                  {normalizarQrCode(qrCode).startsWith('data:image') ? <img src={normalizarQrCode(qrCode)} alt="QR Code WhatsApp" /> : <QRCodeCanvas value={String(qrCode)} size={220} level="L" includeMargin />}
                  <div className="qr-help-text"><strong>QR Code pronto</strong><span>Aguardando leitura pelo celular</span></div>
                </div>
              ) : <div className="qr-loader"><div className="loader-spinner-blue" /><span>Gerando código seguro...</span></div>}
            </div>
          </div>
        </div>
      )}

      {toast && <div className={`system-toast slide-up ${toast.type}`}>
        {toast.type === 'success' && <Icons.TrendingUp />}
        {toast.type === 'error' && <Icons.Close />}
        {toast.message}
      </div>}
    </div>
  );
}

// ============================================================================
// CSS (MOTOR VISUAL VERSÃO 5.0 - PREMIUM SAAS)
// ============================================================================
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

    :root {
      /* Paleta de Cores Premium (Slate / Indigo) */
      --bg-color: #F8FAFC;
      --surface: #FFFFFF;
      --surface-hover: #F1F5F9;
      --border: #E2E8F0;
      
      --text-main: #0F172A;
      --text-sec: #64748B;
      --text-soft: #94A3B8;
      
      --primary: #4F46E5;
      --primary-hover: #4338CA;
      --primary-soft: #EEF2FF;
      
      --green: #10B981;
      --green-soft: #ECFDF5;
      --red: #EF4444;
      --red-soft: #FEF2F2;
      --amber: #F59E0B;
      --amber-soft: #FFFBEB;
      --blue: #3B82F6;
      --blue-soft: #EFF6FF;
      --purple: #8B5CF6;
      --purple-soft: #F5F3FF;
      
      --sidebar-width: 260px;
      --sidebar-collapsed: 80px;
      --topbar-h: 68px;
      --font: 'Inter', system-ui, -apple-system, sans-serif;
      
      --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
      --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05);
      --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.05), 0 4px 6px -4px rgb(0 0 0 / 0.05);
      --radius-sm: 8px;
      --radius-md: 12px;
      --radius-lg: 16px;
      --radius-full: 9999px;
    }

    * { box-sizing: border-box; margin: 0; padding: 0; outline: none; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
    html, body, #root { width: 100%; height: 100dvh; overflow: hidden; background: var(--bg-color); color: var(--text-main); font-family: var(--font); font-size: 14px; }
    button, input, select { font: inherit; border: none; background: none; }
    button { cursor: pointer; -webkit-tap-highlight-color: transparent; }
    button:disabled { opacity: 0.5; cursor: not-allowed; }

    /* Scrollbars Elegantes */
    .custom-scrollbar { scrollbar-width: thin; scrollbar-color: #CBD5E1 transparent; }
    .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
    .custom-scrollbar::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: var(--radius-full); }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94A3B8; }

    /* Layout Base */
    .vizum-app-shell { display: flex; width: 100vw; height: 100dvh; background: var(--bg-color); }
    .app-main { flex: 1; display: flex; flex-direction: column; min-width: 0; }
    .content-scroll { flex: 1; overflow-y: auto; overflow-x: hidden; padding: 32px; }
    .module-container { max-width: 1400px; margin: 0 auto; background: var(--surface); border-radius: var(--radius-lg); border: 1px solid var(--border); box-shadow: var(--shadow-sm); padding: 32px; min-height: calc(100vh - 140px); }

    /* Animações Globais */
    .fade-in { animation: fadeIn 0.3s ease-out forwards; }
    .slide-up { animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
    .slide-down { animation: slideDown 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
    
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes slideUp { from { opacity: 0; transform: translateY(12px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
    @keyframes slideDown { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }

    /* =========================================================================
       SPLASH SCREEN (Estilo Apple / Linear)
       ========================================================================= */
    .splash-overlay {
      position: fixed; inset: 0; z-index: 99999;
      background: var(--surface);
      display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 24px;
      transition: opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1), visibility 0.6s;
    }
    .splash-overlay.fade-out { opacity: 0; visibility: hidden; pointer-events: none; }
    
    .simple-splash-card {
      width: 100px; height: 100px; border-radius: 28px;
      background: var(--surface);
      box-shadow: 0 20px 40px rgba(15, 23, 42, 0.08), inset 0 0 0 1px rgba(15, 23, 42, 0.05);
      display: grid; place-items: center; position: relative;
    }
    .splash-logo-img { width: 56px; height: 56px; object-fit: contain; z-index: 2; animation: pulseLogo 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
    .splash-loader-line { position: absolute; bottom: -32px; left: 50%; transform: translateX(-50%); width: 120px; height: 3px; background: var(--border); border-radius: var(--radius-full); overflow: hidden; }
    .splash-loader-line::before { content: ''; position: absolute; inset: 0; width: 40%; background: var(--primary); border-radius: inherit; animation: splashLine 1.5s ease-in-out infinite; }
    
    .splash-text { text-align: center; margin-top: 32px; }
    .splash-text h2 { font-size: 24px; font-weight: 800; color: var(--text-main); letter-spacing: -0.03em; margin-bottom: 8px; }
    .splash-text p { font-size: 14px; font-weight: 500; color: var(--text-sec); }
    
    @keyframes pulseLogo { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(0.95); opacity: 0.8; } }
    @keyframes splashLine { 0% { transform: translateX(-100%); } 50% { transform: translateX(60%); } 100% { transform: translateX(250%); } }

    /* =========================================================================
       SIDEBAR & NAVEGAÇÃO
       ========================================================================= */
    .app-sidebar {
      width: var(--sidebar-width); flex-shrink: 0; background: var(--surface);
      border-right: 1px solid var(--border); display: flex; flex-direction: column;
      transition: width 0.3s cubic-bezier(0.16, 1, 0.3, 1); z-index: 50;
    }
    .sidebar-collapsed .app-sidebar { width: var(--sidebar-collapsed); }
    
    .brand-header { height: var(--topbar-h); display: flex; align-items: center; padding: 0 24px; border-bottom: 1px solid transparent; }
    .sidebar-collapsed .brand-header { padding: 0; justify-content: center; }
    
    .brand-logo { display: flex; align-items: center; gap: 12px; }
    .brand-logo-mark { width: 32px; height: 32px; border-radius: 8px; background: var(--primary); display: grid; place-items: center; box-shadow: 0 4px 12px rgba(79, 70, 229, 0.25); }
    .logo-icon-invert { width: 20px; height: 20px; filter: brightness(0) invert(1); }
    .brand-text { font-size: 18px; font-weight: 800; color: var(--text-main); letter-spacing: -0.03em; }
    .sidebar-collapsed .brand-text { display: none; }

    .nav-menu { flex: 1; padding: 24px 16px; display: flex; flex-direction: column; gap: 32px; overflow-y: auto; }
    .nav-section-header { padding: 0 12px 8px; }
    .nav-section-title { font-size: 11px; font-weight: 700; color: var(--text-soft); text-transform: uppercase; letter-spacing: 0.08em; }
    .sidebar-collapsed .nav-section-header { display: none; }
    
    .nav-section-items { display: flex; flex-direction: column; gap: 4px; }
    .nav-item {
      position: relative; width: 100%; height: 40px; display: flex; align-items: center; gap: 12px; padding: 0 12px;
      border-radius: var(--radius-sm); color: var(--text-sec); font-size: 14px; font-weight: 600;
      transition: all 0.2s ease; text-align: left;
    }
    .nav-item:hover { background: var(--surface-hover); color: var(--text-main); }
    .nav-item.active { background: var(--primary-soft); color: var(--primary); font-weight: 700; }
    
    .nav-icon { width: 20px; display: grid; place-items: center; opacity: 0.8; }
    .nav-item.active .nav-icon { opacity: 1; }
    .active-indicator { position: absolute; left: -16px; top: 50%; transform: translateY(-50%); width: 4px; height: 20px; background: var(--primary); border-radius: 0 4px 4px 0; }
    
    .sidebar-collapsed .nav-item { width: 44px; height: 44px; justify-content: center; padding: 0; margin: 0 auto; border-radius: var(--radius-md); }
    .sidebar-collapsed .nav-label { display: none; }
    .sidebar-collapsed .active-indicator { display: none; }
    .sidebar-collapsed .nav-item.active { box-shadow: inset 0 0 0 1px var(--primary-soft); }

    .sidebar-footer { padding: 16px; border-top: 1px solid var(--border); }
    .btn-sidebar-logout { color: var(--text-sec); }
    .btn-sidebar-logout:hover { color: var(--red); background: var(--red-soft); }

    /* =========================================================================
       TOPBAR & GLASSMORPHISM
       ========================================================================= */
    .app-topbar {
      height: var(--topbar-h); min-height: var(--topbar-h);
      display: flex; align-items: center; justify-content: space-between; padding: 0 32px;
      background: rgba(255, 255, 255, 0.85); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
      border-bottom: 1px solid var(--border); z-index: 40; position: sticky; top: 0;
    }
    
    .topbar-left { display: flex; align-items: center; gap: 20px; min-width: 200px; }
    .icon-btn { width: 36px; height: 36px; border-radius: var(--radius-sm); display: grid; place-items: center; color: var(--text-sec); transition: 0.2s; }
    .icon-btn:hover { background: var(--surface-hover); color: var(--text-main); }
    .desktop-only { display: grid; } .mobile-only { display: none; }
    
    .route-context { display: flex; flex-direction: column; }
    .route-context span { font-size: 11px; font-weight: 600; color: var(--text-soft); text-transform: uppercase; letter-spacing: 0.05em; }
    .route-context strong { font-size: 16px; font-weight: 800; color: var(--text-main); letter-spacing: -0.02em; margin-top: 2px; }

    /* Barra de Pesquisa Estilo Spotlight */
    .topbar-center { flex: 1; max-width: 560px; margin: 0 32px; position: relative; }
    .search-omnibox {
      height: 40px; display: flex; align-items: center; gap: 12px; padding: 0 16px;
      background: var(--surface-hover); border-radius: var(--radius-full);
      border: 1px solid transparent; color: var(--text-soft); transition: all 0.2s;
    }
    .search-omnibox:hover { background: #E2E8F0; }
    .search-omnibox.focused { background: var(--surface); border-color: var(--primary); box-shadow: 0 0 0 3px var(--primary-soft); color: var(--primary); }
    .search-omnibox input { flex: 1; font-size: 14px; font-weight: 500; color: var(--text-main); }
    .search-omnibox input::placeholder { color: var(--text-soft); }
    
    .icon-btn-mic { width: 28px; height: 28px; border-radius: 50%; display: grid; place-items: center; color: var(--text-sec); }
    .icon-btn-mic:hover { background: var(--border); color: var(--text-main); }
    .icon-btn-mic.listening { background: var(--red); color: white; animation: pulseMic 1.5s infinite; }
    @keyframes pulseMic { 0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(239,68,68,0.4); } 50% { transform: scale(1.05); box-shadow: 0 0 0 6px rgba(239,68,68,0); } }

    .search-dropdown {
      position: absolute; top: calc(100% + 8px); left: 0; width: 100%;
      background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg);
      box-shadow: var(--shadow-lg); padding: 8px; z-index: 50;
    }
    .search-result-item { display: flex; align-items: center; gap: 12px; padding: 12px; border-radius: var(--radius-sm); cursor: pointer; transition: 0.15s; }
    .search-result-item:hover { background: var(--surface-hover); }
    .result-icon { width: 36px; height: 36px; border-radius: var(--radius-sm); background: var(--primary-soft); color: var(--primary); display: grid; place-items: center; }
    .result-name { display: block; font-size: 14px; font-weight: 700; color: var(--text-main); }
    .result-sub { display: block; font-size: 12px; color: var(--text-sec); margin-top: 2px; }
    .search-empty { padding: 16px; text-align: center; color: var(--text-sec); font-weight: 500; font-size: 14px; }

    /* =========================================================================
       PERFIL & MENU DROPDOWN (Estilo Popover macOS)
       ========================================================================= */
    .topbar-right { display: flex; align-items: center; }
    .user-profile-wrapper { position: relative; }
    .avatar-btn {
      width: 40px; height: 40px; border-radius: 50%;
      background: linear-gradient(135deg, var(--primary-soft), #E0E7FF);
      color: var(--primary); border: 1px solid var(--border);
      font-size: 14px; font-weight: 800; display: grid; place-items: center;
      transition: 0.2s; position: relative;
    }
    .avatar-btn:hover { box-shadow: 0 0 0 3px var(--primary-soft); transform: translateY(-1px); }
    .status-dot { position: absolute; bottom: 0; right: 0; width: 12px; height: 12px; border-radius: 50%; border: 2px solid var(--surface); }
    .status-dot.online { background: var(--green); } .status-dot.offline { background: var(--text-soft); }

    .profile-menu {
      position: absolute; top: calc(100% + 12px); right: 0; width: 340px;
      background: rgba(255,255,255,0.95); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
      border: 1px solid var(--border); border-radius: var(--radius-lg);
      box-shadow: 0 20px 40px rgba(0,0,0,0.1); overflow: hidden; transform-origin: top right;
    }
    .profile-menu-head { display: flex; align-items: center; gap: 16px; padding: 20px; border-bottom: 1px solid var(--border); background: var(--surface); }
    .profile-large-avatar { width: 48px; height: 48px; border-radius: 50%; background: var(--primary-soft); color: var(--primary); display: grid; place-items: center; font-size: 16px; font-weight: 800; }
    .profile-account-copy strong { display: block; font-size: 15px; font-weight: 800; color: var(--text-main); }
    .profile-account-copy span { display: block; font-size: 13px; color: var(--text-sec); margin-top: 2px; }
    .profile-account-copy small { display: inline-block; margin-top: 8px; padding: 2px 8px; border-radius: var(--radius-full); background: var(--surface-hover); color: var(--text-sec); font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; }
    
    .profile-menu-section { padding: 12px; border-bottom: 1px solid var(--border); }
    .profile-wa-row {
      width: 100%; display: grid; grid-template-columns: 40px 1fr; gap: 12px; align-items: center;
      padding: 12px; border-radius: var(--radius-md); background: transparent; transition: 0.2s; text-align: left;
    }
    .profile-wa-row:hover { background: var(--surface-hover); }
    .profile-wa-icon { width: 40px; height: 40px; border-radius: var(--radius-sm); background: var(--surface-hover); color: var(--text-sec); display: grid; place-items: center; }
    .profile-wa-row.connected .profile-wa-icon { background: var(--green-soft); color: var(--green); }
    .profile-wa-copy strong { display: block; font-size: 14px; font-weight: 700; color: var(--text-main); }
    .profile-wa-copy small { display: block; font-size: 12px; color: var(--text-sec); margin-top: 2px; }
    .profile-wa-copy em { display: block; font-size: 11px; color: var(--text-soft); font-style: normal; margin-top: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .profile-wa-action { grid-column: 2; margin-top: 4px; display: inline-block; padding: 4px 10px; border-radius: var(--radius-full); background: var(--surface); border: 1px solid var(--border); color: var(--text-main); font-size: 11px; font-weight: 700; }
    
    .profile-menu-actions { padding: 8px; display: flex; flex-direction: column; gap: 2px; }
    .profile-menu-actions button { display: flex; align-items: center; gap: 12px; width: 100%; padding: 10px 12px; border-radius: var(--radius-sm); font-size: 14px; font-weight: 600; color: var(--text-sec); transition: 0.2s; }
    .profile-menu-actions button:hover { background: var(--surface-hover); color: var(--text-main); }
    .profile-menu-actions button.danger { color: var(--red); }
    .profile-menu-actions button.danger:hover { background: var(--red-soft); }

    /* =========================================================================
       DASHBOARD OPERACIONAL (Layout Clean Premium)
       ========================================================================= */
    .clean-dashboard { max-width: 1400px; margin: 0 auto; display: flex; flex-direction: column; gap: 32px; }
    
    /* Header / Hero */
    .clean-header-card { display: flex; justify-content: space-between; align-items: flex-end; gap: 24px; padding-bottom: 24px; border-bottom: 1px solid var(--border); flex-wrap: wrap; }
    .ops-eyebrow { display: inline-block; padding: 4px 12px; border-radius: var(--radius-full); background: var(--primary-soft); color: var(--primary); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 12px; }
    .clean-header-info h1 { font-size: 32px; font-weight: 800; color: var(--text-main); letter-spacing: -0.03em; line-height: 1.1; margin-bottom: 8px; }
    .clean-header-info p { font-size: 15px; color: var(--text-sec); max-width: 600px; line-height: 1.5; }
    
    .clean-header-pills { display: flex; gap: 8px; margin-top: 16px; }
    .pill { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: var(--radius-full); background: var(--surface); border: 1px solid var(--border); font-size: 12px; font-weight: 600; color: var(--text-sec); }
    .pill.ok { background: var(--green-soft); border-color: #A7F3D0; color: #059669; }
    .pill svg { width: 14px; height: 14px; }

    .clean-header-actions { display: flex; flex-direction: column; gap: 16px; min-width: 300px; }
    .clean-store-chip { display: flex; align-items: center; gap: 8px; padding: 0 16px; height: 44px; border-radius: var(--radius-md); background: var(--surface); border: 1px solid var(--border); box-shadow: var(--shadow-sm); transition: 0.2s; }
    .clean-store-chip:hover { border-color: #CBD5E1; }
    .clean-store-chip select { flex: 1; font-size: 14px; font-weight: 600; color: var(--text-main); appearance: none; cursor: pointer; }
    
    .clean-action-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .clean-primary-btn, .clean-secondary-btn { height: 44px; display: inline-flex; align-items: center; justify-content: center; gap: 8px; border-radius: var(--radius-md); font-size: 14px; font-weight: 600; transition: all 0.2s; }
    .clean-primary-btn { background: var(--primary); color: white; box-shadow: 0 4px 12px rgba(79, 70, 229, 0.2); }
    .clean-primary-btn:hover { background: var(--primary-hover); transform: translateY(-1px); box-shadow: 0 6px 16px rgba(79, 70, 229, 0.3); }
    .clean-secondary-btn { background: var(--surface); border: 1px solid var(--border); color: var(--text-main); box-shadow: var(--shadow-sm); }
    .clean-secondary-btn:hover { background: var(--surface-hover); }

    /* KPIs */
    .clean-kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
    .clean-metric-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 24px; box-shadow: var(--shadow-sm); transition: transform 0.2s, box-shadow 0.2s; }
    .clean-metric-card:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); }
    .metric-header { display: flex; justify-content: space-between; align-items: flex-start; }
    .metric-header span { font-size: 13px; font-weight: 600; color: var(--text-sec); }
    .metric-icon-bg { width: 12px; height: 12px; border-radius: 50%; }
    .metric-icon-bg.blue { background: var(--blue); } .metric-icon-bg.green { background: var(--green); } .metric-icon-bg.red { background: var(--red); } .metric-icon-bg.amber { background: var(--amber); }
    .clean-metric-card strong { display: block; font-size: 32px; font-weight: 800; color: var(--text-main); margin: 16px 0 4px; line-height: 1; letter-spacing: -0.02em; }
    .clean-metric-card p { font-size: 13px; color: var(--text-soft); font-weight: 500; }

    /* Layout Calendário e Eventos */
    .ops-calendar-layout { display: grid; grid-template-columns: 1.5fr 1fr; gap: 24px; align-items: start; }
    .clean-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 28px; box-shadow: var(--shadow-sm); }
    
    .clean-card-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 24px; }
    .clean-card-header h3 { font-size: 18px; font-weight: 800; color: var(--text-main); letter-spacing: -0.02em; }
    .clean-card-header span { display: block; font-size: 13px; color: var(--text-sec); margin-top: 4px; }
    
    .ops-calendar-actions { display: flex; align-items: center; gap: 12px; background: var(--surface-hover); padding: 4px; border-radius: var(--radius-md); }
    .ops-calendar-actions strong { min-width: 130px; text-align: center; font-size: 14px; font-weight: 700; color: var(--text-main); }
    .ops-month-btn { width: 32px; height: 32px; border-radius: var(--radius-sm); display: grid; place-items: center; background: transparent; color: var(--text-sec); transition: 0.2s; }
    .ops-month-btn:hover { background: var(--surface); box-shadow: var(--shadow-sm); color: var(--text-main); }
    .ops-month-btn svg { transform: rotate(90deg); } .ops-month-btn.next svg { transform: rotate(-90deg); }
    .current-month-btn { padding: 0 12px; height: 32px; font-size: 12px; font-weight: 600; color: var(--primary); border-radius: var(--radius-sm); background: transparent; }
    .current-month-btn:hover { background: var(--primary-soft); }

    .ops-calendar-weekdays { display: grid; grid-template-columns: repeat(7, 1fr); gap: 8px; margin-bottom: 12px; }
    .ops-calendar-weekdays span { text-align: center; font-size: 11px; font-weight: 700; color: var(--text-soft); text-transform: uppercase; }
    
    .ops-calendar-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 8px; }
    .ops-day-cell { min-height: 90px; padding: 12px; border-radius: var(--radius-md); background: var(--surface-hover); border: 1px solid transparent; display: flex; flex-direction: column; justify-content: space-between; align-items: stretch; transition: 0.2s; cursor: pointer; }
    .ops-day-cell.empty { background: transparent; cursor: default; }
    .ops-day-cell:not(.empty):hover { background: var(--surface); border-color: var(--border); box-shadow: var(--shadow-sm); transform: translateY(-2px); }
    .ops-day-cell.today { background: var(--primary-soft); border-color: #C7D2FE; }
    .ops-day-cell.selected { background: var(--text-main); color: white; box-shadow: var(--shadow-md); transform: translateY(-2px); }
    
    .ops-day-number { font-size: 15px; font-weight: 700; color: inherit; align-self: flex-start; }
    .ops-day-cell.today .ops-day-number { color: var(--primary); }
    .ops-day-cell.selected .ops-day-number { color: white; }
    
    .event-count { align-self: flex-end; padding: 2px 8px; border-radius: var(--radius-full); background: rgba(0,0,0,0.05); font-size: 11px; font-weight: 700; }
    .ops-day-cell.selected .event-count { background: rgba(255,255,255,0.2); }
    
    .ops-day-dots { display: flex; gap: 4px; margin-top: auto; padding-top: 8px; }
    .ops-day-dots i { width: 6px; height: 6px; border-radius: 50%; }
    .dot-red { background: var(--red); } .dot-green { background: var(--green); } .dot-blue { background: var(--blue); }

    /* Lista de Eventos Diários */
    .clean-count-badge { padding: 4px 12px; border-radius: var(--radius-full); background: var(--surface-hover); color: var(--text-main); font-size: 13px; font-weight: 700; border: 1px solid var(--border); }
    .ops-event-list { display: flex; flex-direction: column; gap: 12px; max-height: 500px; padding-right: 8px; }
    
    .ops-event-item { display: flex; gap: 16px; padding: 16px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-md); text-align: left; transition: 0.2s; cursor: pointer; }
    .ops-event-item:hover { border-color: #CBD5E1; box-shadow: var(--shadow-sm); transform: translateX(4px); }
    
    .event-time-col { display: flex; flex-direction: column; align-items: center; min-width: 48px; }
    .ops-event-time { font-size: 13px; font-weight: 700; color: var(--text-main); }
    .event-line-connector { flex: 1; width: 2px; background: var(--border); margin-top: 8px; border-radius: var(--radius-full); }
    
    .ops-event-copy { flex: 1; }
    .ops-event-copy strong { display: block; font-size: 15px; font-weight: 700; color: var(--text-main); margin-bottom: 4px; }
    .ops-event-copy small { display: block; font-size: 13px; color: var(--text-sec); font-weight: 500; }
    .ops-event-copy em { display: block; font-size: 12px; color: var(--text-soft); font-style: normal; margin-top: 4px; }
    
    .ops-event-details { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 12px; padding-top: 12px; border-top: 1px dashed var(--border); }
    .ops-event-details i { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--text-sec); font-style: normal; font-weight: 500; }
    .ops-event-details svg { width: 14px; height: 14px; opacity: 0.7; }
    
    .tone-red .event-line-connector { background: var(--red); }
    .tone-green .event-line-connector { background: var(--green); }
    .tone-blue .event-line-connector { background: var(--blue); }
    .tone-purple .event-line-connector { background: var(--purple); }

    .clean-empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 48px 24px; text-align: center; background: var(--surface-hover); border-radius: var(--radius-md); border: 1px dashed var(--border); }
    .clean-empty-state svg { width: 32px; height: 32px; color: var(--text-soft); margin-bottom: 16px; }
    .clean-empty-state strong { font-size: 15px; font-weight: 700; color: var(--text-main); margin-bottom: 8px; }
    .clean-empty-state p { font-size: 13px; color: var(--text-sec); margin-bottom: 24px; max-width: 250px; }

    /* =========================================================================
       MODAIS & TOASTS
       ========================================================================= */
    .system-toast { position: fixed; bottom: 32px; left: 50%; transform: translateX(-50%); display: flex; align-items: center; gap: 12px; padding: 14px 24px; border-radius: var(--radius-full); font-size: 14px; font-weight: 600; color: white; background: var(--text-main); z-index: 99999; box-shadow: 0 12px 24px rgba(0,0,0,0.15); }
    .system-toast.success { background: var(--text-main); }
    .system-toast.error { background: var(--red); }
    .system-toast svg { width: 18px; height: 18px; }

    .modal-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.4); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); z-index: 9999; display: grid; place-items: center; padding: 24px; }
    .modal-box { width: 100%; max-width: 480px; background: var(--surface); border-radius: 24px; overflow: hidden; box-shadow: 0 24px 48px rgba(0,0,0,0.12); }
    
    .modal-top { padding: 24px; border-bottom: 1px solid var(--border); display: flex; justify-content: space-between; align-items: flex-start; }
    .modal-titles h3 { font-size: 20px; font-weight: 800; color: var(--text-main); letter-spacing: -0.02em; }
    .modal-titles p { font-size: 14px; color: var(--text-sec); margin-top: 6px; }
    .icon-btn-close { width: 36px; height: 36px; border-radius: 50%; display: grid; place-items: center; color: var(--text-sec); background: var(--surface-hover); transition: 0.2s; }
    .icon-btn-close:hover { background: var(--border); color: var(--text-main); }

    .channel-selector-list { max-height: 400px; padding: 24px; display: flex; flex-direction: column; gap: 16px; background: var(--bg-color); }
    .channel-selector-item { display: flex; align-items: center; gap: 16px; padding: 20px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-md); box-shadow: var(--shadow-sm); }
    .channel-selector-status-dot { width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; box-shadow: inset 0 0 0 1px rgba(0,0,0,0.1); }
    .channel-selector-status-dot.online { background: var(--green); } .channel-selector-status-dot.offline { background: var(--border); } .channel-selector-status-dot.loading { background: var(--amber); } .channel-selector-status-dot.qr { background: var(--blue); }
    
    .channel-selector-copy { flex: 1; }
    .channel-selector-copy strong { display: block; font-size: 15px; font-weight: 700; color: var(--text-main); }
    .channel-selector-copy span { display: block; font-size: 13px; color: var(--text-sec); margin: 4px 0; }
    .channel-selector-copy small { font-size: 12px; font-weight: 600; color: var(--text-soft); }
    
    .channel-action { padding: 8px 16px; border-radius: var(--radius-sm); font-size: 13px; font-weight: 600; transition: 0.2s; }
    .channel-action.primary { background: var(--primary); color: white; }
    .channel-action.primary:hover:not(:disabled) { background: var(--primary-hover); }
    .channel-action.secondary { background: var(--surface); border: 1px solid var(--border); color: var(--text-main); }
    .channel-action.secondary:hover:not(:disabled) { background: var(--surface-hover); }
    
    .channel-selector-empty { padding: 48px 24px; text-align: center; color: var(--text-sec); display: flex; flex-direction: column; align-items: center; gap: 16px; }
    .channel-selector-empty svg { width: 40px; height: 40px; color: var(--text-soft); }
    .channel-selector-empty strong { font-size: 16px; font-weight: 700; color: var(--text-main); }
    
    .channel-selector-footer { padding: 20px 24px; border-top: 1px solid var(--border); display: flex; justify-content: flex-end; background: var(--surface); }

    /* Modal QR Code */
    .qr-container { padding: 48px; display: grid; place-items: center; background: var(--bg-color); }
    .qr-code-fade-in { display: flex; flex-direction: column; align-items: center; gap: 24px; }
    .qr-code-fade-in canvas, .qr-code-fade-in img { border-radius: 16px; padding: 12px; background: white; box-shadow: var(--shadow-md); border: 1px solid var(--border); }
    .qr-help-text { text-align: center; }
    .qr-help-text strong { display: block; font-size: 16px; font-weight: 700; color: var(--text-main); }
    .qr-help-text span { display: block; font-size: 14px; color: var(--text-sec); margin-top: 4px; }
    
    .loader-spinner-blue { width: 32px; height: 32px; border: 3px solid var(--primary-soft); border-top-color: var(--primary); border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 16px; }
    @keyframes spin { to { transform: rotate(360deg); } }

    /* =========================================================================
       RESPONSIVIDADE
       ========================================================================= */
    @media (max-width: 1200px) {
      .clean-kpi-grid { grid-template-columns: repeat(2, 1fr); }
      .ops-calendar-layout { grid-template-columns: 1fr; }
    }


    /* =========================================================================
       SISTEMA VISUAL UNIFICADO — TODOS OS COMPONENTES HERDAM A MESMA PLATAFORMA
       ========================================================================= */
    body { background: #F4F6F8; color: var(--text-main); }

    .vizum-app-shell {
      min-width: 0;
      background: #F4F6F8;
    }

    .app-sidebar {
      background: #111827;
      border-right: 0;
      box-shadow: none;
    }

    .brand-header {
      min-height: var(--topbar-h);
      border-bottom: 1px solid rgba(255,255,255,.08);
    }

    .brand-text { color: #FFFFFF; font-size: 15px; letter-spacing: -.02em; }
    .nav-section-title { color: rgba(255,255,255,.42); letter-spacing: .08em; }
    .nav-item { color: rgba(255,255,255,.68); border: 0; box-shadow: none; }
    .nav-item:hover { color: #FFFFFF; background: rgba(255,255,255,.06); transform: none; }
    .nav-item.active {
      color: #FFFFFF;
      background: rgba(255,255,255,.10);
      box-shadow: inset 3px 0 0 #FFFFFF;
    }
    .nav-item.active .nav-icon { color: #FFFFFF; }
    .nav-item.featured:not(.active) { background: transparent; }
    .active-indicator { display: none; }
    .sidebar-footer { border-top: 1px solid rgba(255,255,255,.08); }

    .app-main { min-width: 0; background: #F4F6F8; }
    .app-topbar {
      background: rgba(255,255,255,.96);
      border-bottom: 1px solid #E5E7EB;
      box-shadow: none;
      backdrop-filter: blur(16px);
    }

    .route-context span { color: #6B7280; }
    .route-context strong { color: #111827; }
    .search-omnibox { background: #F3F4F6; border-color: transparent; box-shadow: none; }
    .search-omnibox:hover { background: #EEF0F3; }
    .search-omnibox.focused { background: #FFFFFF; border-color: #9CA3AF; box-shadow: 0 0 0 3px rgba(17,24,39,.06); }

    .content-scroll {
      padding: 0;
      overscroll-behavior: contain;
      background: #F4F6F8;
    }

    .clean-dashboard {
      width: min(100%, 1520px);
      margin: 0 auto;
      padding: 28px 32px 48px;
    }

    .clean-header-card,
    .clean-card,
    .clean-metric-card {
      border-color: #E5E7EB;
      box-shadow: none;
    }

    .clean-header-card {
      background: transparent;
      border: 0;
      border-radius: 0;
      padding: 8px 0 24px;
    }

    .clean-metric-card { border-radius: 12px; }
    .clean-card { border-radius: 14px; }

    .module-workspace {
      width: 100%;
      min-height: calc(100dvh - var(--topbar-h));
      background: #FFFFFF;
    }

    .module-workspace-header {
      position: sticky;
      top: 0;
      z-index: 20;
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      gap: 24px;
      padding: 24px 32px 20px;
      background: rgba(255,255,255,.97);
      border-bottom: 1px solid #E5E7EB;
      backdrop-filter: blur(14px);
    }

    .module-workspace-heading { min-width: 0; }
    .module-breadcrumb {
      display: block;
      margin-bottom: 5px;
      color: #6B7280;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: .08em;
      text-transform: uppercase;
    }
    .module-workspace-heading h1 {
      color: #111827;
      font-size: clamp(22px, 2vw, 30px);
      font-weight: 750;
      line-height: 1.15;
      letter-spacing: -.035em;
    }
    .module-workspace-heading p {
      max-width: 720px;
      margin-top: 7px;
      color: #6B7280;
      font-size: 14px;
      line-height: 1.5;
    }

    .module-back-button {
      flex: 0 0 auto;
      min-height: 38px;
      padding: 0 14px;
      border: 1px solid #D1D5DB;
      border-radius: 9px;
      background: #FFFFFF;
      color: #374151;
      font-size: 13px;
      font-weight: 650;
      transition: background .16s ease, border-color .16s ease;
    }
    .module-back-button:hover { background: #F9FAFB; border-color: #9CA3AF; }

    .module-workspace-body {
      width: 100%;
      min-width: 0;
      padding: 28px 32px 48px;
      background: #FFFFFF;
    }

    /* Neutraliza “plataformas dentro da plataforma” sem destruir os componentes. */
    .module-workspace-body > * { width: 100%; max-width: none; margin-inline: 0; }
    .module-workspace-body :where(.container, .page-container, .dashboard-container, .content-container, .main-container) {
      width: 100%;
      max-width: none;
      margin-inline: 0;
    }
    .module-workspace-body :where(.page-header, .header-card, .hero-card):first-child {
      margin-top: 0;
    }
    .module-workspace-body :where(.card, .panel, .box, .section-card) {
      box-shadow: none;
    }
    .module-workspace-body :where(button, input, select, textarea) { font-family: var(--font); }
    .module-workspace-body :where(input, select, textarea) {
      border-color: #D1D5DB;
      border-radius: 9px;
      box-shadow: none;
    }
    .module-workspace-body :where(input, select, textarea):focus {
      border-color: #6B7280;
      box-shadow: 0 0 0 3px rgba(17,24,39,.06);
    }
    .module-workspace-body :where(table) { border-collapse: separate; border-spacing: 0; }
    .module-workspace-body :where(th) {
      background: #F9FAFB;
      color: #4B5563;
      font-size: 11px;
      letter-spacing: .04em;
      text-transform: uppercase;
    }

    .splash-overlay { background: #FFFFFF; transition: opacity .22s ease; }
    .simple-splash-card { box-shadow: none; border: 0; }

    @media (max-width: 768px) {
      .module-workspace-header { position: static; align-items: flex-start; padding: 20px 18px 16px; }
      .module-workspace-heading p { font-size: 13px; }
      .module-back-button { display: none; }
      .module-workspace-body { padding: 18px 14px 32px; }
      .clean-dashboard { padding: 18px 14px 32px; }
      .app-sidebar { position: fixed; left: -100%; top: 0; bottom: 0; z-index: 100; box-shadow: var(--shadow-lg); transition: left 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
      .app-sidebar.open { left: 0; width: 280px; }
      .mobile-sidebar-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.4); backdrop-filter: blur(2px); z-index: 90; opacity: 0; visibility: hidden; transition: 0.3s; }
      .mobile-sidebar-overlay.active { opacity: 1; visibility: visible; }
      
      .mobile-only { display: grid; } .desktop-only { display: none; }
      .app-topbar { padding: 0 16px; }
      .topbar-center { margin: 0 16px; }
      .search-omnibox input { font-size: 14px; }
      
      .profile-menu { position: fixed; top: 72px; right: 16px; left: 16px; width: auto; transform-origin: top; }
      
      .content-scroll { padding: 16px; }
      .clean-header-card { flex-direction: column; align-items: flex-start; gap: 20px; }
      .clean-header-actions { width: 100%; }
      .clean-action-row { grid-template-columns: 1fr; }
      
      .ops-calendar-top { flex-direction: column; align-items: flex-start; gap: 16px; }
      .ops-calendar-actions { width: 100%; justify-content: space-between; }
      .ops-calendar-actions strong { flex: 1; text-align: center; }
      
      .ops-day-cell { min-height: 60px; padding: 8px; }
      .ops-day-number { font-size: 13px; }
      .ops-event-item { flex-direction: column; gap: 12px; }
      .event-time-col { flex-direction: row; align-items: center; width: 100%; gap: 12px; border-bottom: 1px solid var(--border); padding-bottom: 8px; }
      .event-line-connector { display: none; }
    }

    /* =========================================================================
       LAPIDAÇÃO V2.3 — INTERFACE BRANCA, MENU LEVE E DASHBOARD SEM CARDS
       ========================================================================= */
    :root {
      --bg-color: #FFFFFF;
      --surface: #FFFFFF;
      --surface-hover: #F6F8F8;
      --border: #E6EAEA;
      --text-main: #172020;
      --text-sec: #526060;
      --text-soft: #7C8989;
      --primary: #146C68;
      --primary-hover: #0F5A57;
      --primary-soft: #EAF5F4;
    }

    body,
    .vizum-app-shell,
    .app-main,
    .content-scroll,
    .module-workspace,
    .module-workspace-body {
      background: #FFFFFF !important;
    }

    .app-sidebar {
      background: #173C3A !important;
      border-right: 0 !important;
      color: #FFFFFF;
    }

    .brand-header {
      border-bottom: 1px solid rgba(255,255,255,.10) !important;
    }

    .brand-logo-mark {
      width: 34px;
      height: 34px;
      background: rgba(255,255,255,.10) !important;
      box-shadow: none !important;
    }

    .logo-icon-invert {
      width: 22px;
      height: 22px;
      filter: brightness(0) invert(1) !important;
      opacity: .96;
    }

    .brand-text {
      color: #FFFFFF !important;
    }

    .nav-section-title {
      color: rgba(255,255,255,.48) !important;
    }

    .nav-item {
      color: rgba(255,255,255,.74) !important;
    }

    .nav-item:hover {
      color: #FFFFFF !important;
      background: rgba(255,255,255,.07) !important;
    }

    .nav-item.active {
      color: #FFFFFF !important;
      background: rgba(255,255,255,.13) !important;
      box-shadow: inset 3px 0 0 #7ED8D2 !important;
    }

    .nav-item.active .nav-icon {
      color: #FFFFFF !important;
    }

    .sidebar-footer {
      border-top: 1px solid rgba(255,255,255,.10) !important;
    }

    .app-topbar {
      background: #FFFFFF !important;
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
      border-bottom: 1px solid #E8ECEC !important;
      box-shadow: none !important;
    }

    .search-omnibox {
      background: #F4F7F7 !important;
      border: 1px solid transparent !important;
      border-radius: 10px !important;
      box-shadow: none !important;
    }

    .search-omnibox:hover {
      background: #EEF3F2 !important;
    }

    .search-omnibox.focused {
      background: #FFFFFF !important;
      border-color: #8BB7B4 !important;
      color: var(--primary) !important;
      box-shadow: 0 0 0 3px rgba(20,108,104,.08) !important;
    }

    .module-workspace {
      min-height: calc(100dvh - var(--topbar-h));
    }

    .module-workspace-header,
    .module-back-button {
      display: none !important;
    }

    .module-workspace-body {
      padding: 0 !important;
    }

    .clean-dashboard {
      width: 100% !important;
      max-width: none !important;
      margin: 0 !important;
      padding: 22px 28px 36px !important;
      gap: 18px !important;
      background: #FFFFFF !important;
    }

    .dashboard-welcome {
      padding: 0 0 4px;
      border: 0;
      background: transparent;
    }

    .dashboard-welcome h1 {
      margin: 0;
      color: #172020;
      font-size: 20px;
      font-weight: 700;
      line-height: 1.25;
      letter-spacing: -.025em;
    }

    .ops-calendar-layout {
      gap: 24px;
      align-items: start;
    }

    .clean-card,
    .ops-calendar-card,
    .ops-day-panel {
      background: #FFFFFF !important;
      border: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
    }

    .ops-calendar-card {
      padding: 0 24px 0 0 !important;
      border-right: 1px solid #E8ECEC !important;
    }

    .ops-day-panel {
      padding: 0 !important;
    }

    .clean-card-header {
      padding: 0 0 16px !important;
      border-bottom: 1px solid #EEF1F1 !important;
    }

    .ops-calendar-weekdays {
      margin-top: 18px;
    }

    .ops-day-cell {
      min-height: 74px !important;
      padding: 9px !important;
      background: #FFFFFF !important;
      border: 1px solid #EDF0F0 !important;
      border-radius: 8px !important;
      box-shadow: none !important;
      transform: none !important;
    }

    .ops-day-cell:not(.empty):hover {
      background: #F7FAF9 !important;
      border-color: #C9D8D7 !important;
      box-shadow: none !important;
      transform: none !important;
    }

    .ops-day-cell.today {
      background: #F0F8F7 !important;
      border-color: #A8CFCC !important;
    }

    .ops-day-cell.selected {
      background: #173C3A !important;
      color: #FFFFFF !important;
      border-color: #173C3A !important;
      box-shadow: none !important;
      transform: none !important;
    }

    .ops-event-list {
      max-height: calc(100dvh - 220px);
    }

    .ops-event-item {
      padding: 13px 0 !important;
      background: #FFFFFF !important;
      border: 0 !important;
      border-bottom: 1px solid #EEF1F1 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      transform: none !important;
    }

    .ops-event-item:hover {
      background: #FAFCFC !important;
      border-color: #E4E9E9 !important;
      box-shadow: none !important;
      transform: none !important;
    }

    .clean-empty-state {
      background: #FFFFFF !important;
      border: 1px dashed #D8E0E0 !important;
      border-radius: 10px !important;
    }

    @media (max-width: 900px) {
      .clean-dashboard {
        padding: 18px 16px 28px !important;
      }

      .ops-calendar-card {
        padding-right: 0 !important;
        border-right: 0 !important;
        border-bottom: 1px solid #E8ECEC !important;
        padding-bottom: 24px !important;
      }
    }


    /* =========================================================================
       LAPIDAÇÃO V2.4 — MENU MONOCROMÁTICO E CALENDÁRIO OPERACIONAL
       ========================================================================= */
    .app-sidebar {
      background: #F1F1F1 !important;
      background-image: none !important;
      border-right: 1px solid #DADADA !important;
      color: #111111 !important;
      box-shadow: none !important;
    }

    .brand-header {
      border-bottom: 1px solid #DADADA !important;
      background: #F1F1F1 !important;
      background-image: none !important;
    }

    .brand-logo-mark {
      background: transparent !important;
      border: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
    }

    .logo-icon-invert {
      filter: grayscale(1) brightness(.15) !important;
      opacity: 1 !important;
    }

    .brand-text,
    .nav-label {
      color: #111111 !important;
    }

    .nav-section-title {
      color: #777777 !important;
    }

    .nav-item,
    .nav-item.featured,
    .nav-item:hover,
    .nav-item:focus,
    .nav-item:active {
      color: #111111 !important;
      background: transparent !important;
      background-image: none !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      transform: none !important;
      transition: color .12s ease, border-color .12s ease !important;
    }

    .nav-item:hover,
    .nav-item:focus-visible {
      color: #000000 !important;
    }

    .nav-item.active {
      color: #000000 !important;
      background: transparent !important;
      background-image: none !important;
      border-left: 2px solid #000000 !important;
      box-shadow: none !important;
    }

    .nav-item:not(.active) {
      border-left: 2px solid transparent !important;
    }

    .nav-item .nav-icon,
    .nav-item:hover .nav-icon,
    .nav-item.active .nav-icon {
      color: currentColor !important;
      background: transparent !important;
      border: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      transform: none !important;
    }

    .nav-item svg,
    .nav-item:hover svg,
    .nav-item.active svg {
      transform: none !important;
      filter: none !important;
    }

    .active-indicator {
      display: none !important;
    }

    .sidebar-footer {
      background: #F1F1F1 !important;
      border-top: 1px solid #DADADA !important;
    }

    .sidebar-footer button,
    .sidebar-footer .profile-trigger {
      color: #111111 !important;
      background: transparent !important;
      box-shadow: none !important;
    }

    .sidebar-footer button:hover,
    .sidebar-footer .profile-trigger:hover {
      background: transparent !important;
    }

    .sidebar-toggle,
    .mobile-menu-btn {
      background: #FFFFFF !important;
      color: #111111 !important;
      border: 1px solid #D9D9D9 !important;
      box-shadow: none !important;
    }


    .dashboard-welcome{display:flex;align-items:flex-start;justify-content:space-between;gap:18px}
    .dashboard-widget-kicker{display:block;margin-bottom:1px;color:#8A9693;font-size:7.8px;font-weight:800;letter-spacing:.07em;text-transform:uppercase}
    .birthday-dashboard-alert{min-width:300px;min-height:76px;padding:9px 10px;display:grid;grid-template-columns:36px minmax(120px,1fr) auto auto;align-items:center;gap:9px;border:1px solid #E3E8E7;border-radius:10px;color:#263232;background:#FFF;text-align:left;box-shadow:none}
    .birthday-dashboard-alert:hover{border-color:#B9CCCA;background:#F8FBFA}
    .birthday-dashboard-alert.pending{border-color:#E8D7A8;background:#FFFCF4}
    .birthday-dashboard-icon{width:36px;height:36px;display:grid;place-items:center;border-radius:8px;color:#3D5755;background:#EEF5F4}
    .birthday-dashboard-alert.pending .birthday-dashboard-icon{color:#8A5A00;background:#FFF2CC}
    .birthday-dashboard-copy{min-width:0;display:grid;gap:2px}
    .birthday-dashboard-copy strong{overflow:hidden;color:#263232;font-size:11.2px;font-weight:750;text-overflow:ellipsis;white-space:nowrap}
    .birthday-dashboard-copy small{overflow:hidden;color:#778181;font-size:9px;font-weight:550;text-overflow:ellipsis;white-space:nowrap}
    .birthday-dashboard-alert.pending .birthday-dashboard-copy small{color:#8A650F}
    .birthday-dashboard-avatars{display:flex;align-items:center;padding-left:4px}
    .birthday-dashboard-avatars i{width:24px;height:24px;margin-left:-5px;display:grid;place-items:center;border:2px solid #FFF;border-radius:50%;color:#53645F;background:#E4EEEB;font-size:7.5px;font-style:normal;font-weight:800}
    .birthday-dashboard-avatars i:first-child{margin-left:0}
    .birthday-dashboard-avatars i.more{color:#76580F;background:#FFF0C4}
    .birthday-dashboard-action{padding:5px 7px;border-radius:6px;color:#455554;background:#EEF1F1;font-size:9px;font-weight:750}
    .birthday-dashboard-alert.pending .birthday-dashboard-action{color:#FFF;background:#8A650F}

    .agenda-dashboard-widget{min-width:330px;min-height:76px;padding:9px 10px;display:grid;grid-template-columns:36px minmax(130px,1fr) auto auto;align-items:center;gap:9px;border:1px solid #DCE5EA;border-radius:10px;color:#263232;background:#FBFDFE;text-align:left;box-shadow:none}
    .agenda-dashboard-widget:hover{border-color:#AEC4D2;background:#F5FAFC}
    .agenda-dashboard-widget.has-items{border-color:#C9DCE8;background:#F7FBFD}
    .agenda-dashboard-icon{width:36px;height:36px;display:grid;place-items:center;border-radius:8px;color:#2F607D;background:#E9F3F8}
    .agenda-dashboard-copy{min-width:0;display:grid;gap:2px}
    .agenda-dashboard-copy strong{overflow:hidden;color:#263632;font-size:11.2px;font-weight:750;text-overflow:ellipsis;white-space:nowrap}
    .agenda-dashboard-copy small{overflow:hidden;color:#71807B;font-size:9px;font-weight:550;text-overflow:ellipsis;white-space:nowrap}
    .agenda-dashboard-list{display:flex;align-items:center;gap:4px}
    .agenda-dashboard-list i{min-width:44px;padding:4px 5px;display:grid;gap:1px;border:1px solid #DDE8ED;border-radius:6px;background:#FFF;font-style:normal;text-align:center}
    .agenda-dashboard-list b{color:#2C5D79;font-size:8.5px;font-weight:800}
    .agenda-dashboard-list em{max-width:48px;overflow:hidden;color:#74827D;font-size:7.5px;font-style:normal;font-weight:650;text-overflow:ellipsis;white-space:nowrap}
    .agenda-dashboard-action{padding:5px 7px;border-radius:6px;color:#2F607D;background:#E7F1F6;font-size:9px;font-weight:750}

    @media(max-width:760px){.dashboard-welcome{align-items:stretch;flex-direction:column}.birthday-dashboard-alert,.agenda-dashboard-widget{width:100%;min-width:0}}


    /* Entregas de hoje com fotos dos clientes */
    .dashboard-welcome {
      align-items: flex-start;
    }

    .dashboard-highlight-row {
      min-width: 0;
      display: flex;
      align-items: stretch;
      justify-content: flex-end;
      gap: 10px;
      flex-wrap: wrap;
    }

    .today-deliveries-highlight {
      min-width: 430px;
      min-height: 76px;
      padding: 8px 10px;
      border: 1px solid #D7E5DF;
      border-radius: 10px;
      background: #F7FBF9;
    }

    .today-deliveries-head {
      display: flex;
      align-items: center;
      gap: 7px;
    }

    .today-deliveries-icon {
      width: 29px;
      height: 29px;
      display: grid;
      place-items: center;
      border-radius: 7px;
      color: #276148;
      background: #E4F2EB;
    }

    .today-deliveries-head > span:last-child {
      display: grid;
      gap: 1px;
    }

    .today-deliveries-head strong {
      color: #263632;
      font-size: 10.5px;
      font-weight: 750;
    }

    .today-deliveries-head small {
      color: #71807B;
      font-size: 8.8px;
      font-weight: 550;
    }

    .today-delivery-clients {
      min-width: 0;
      margin-top: 7px;
      display: flex;
      align-items: flex-start;
      gap: 7px;
      overflow-x: auto;
      padding-bottom: 2px;
    }

    .today-delivery-client {
      width: 48px;
      min-width: 48px;
      padding: 0;
      display: grid;
      justify-items: center;
      gap: 3px;
      border: 0;
      color: #46534F;
      background: transparent;
      cursor: pointer;
    }

    .today-delivery-client > span {
      width: 100%;
      overflow: hidden;
      font-size: 8.2px;
      font-weight: 650;
      text-align: center;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .delivery-client-avatar {
      width: 34px;
      height: 34px;
      padding: 0;
      overflow: hidden;
      display: grid;
      place-items: center;
      flex: 0 0 auto;
      border: 2px solid #FFFFFF;
      border-radius: 50%;
      color: #36564C;
      background: #DCEBE5;
      box-shadow: 0 0 0 1px #BFD4CC;
      font-size: 9px;
      font-weight: 800;
    }

    .delivery-client-avatar img {
      width: 100%;
      height: 100%;
      display: block;
      object-fit: cover;
    }

    .today-delivery-client:hover .delivery-client-avatar {
      box-shadow: 0 0 0 2px #4F806C;
    }

    .today-delivery-more {
      width: 34px;
      height: 34px;
      padding: 0;
      border: 1px solid #BFD4CC;
      border-radius: 50%;
      color: #345D4C;
      background: #FFFFFF;
      font-size: 9px;
      font-weight: 800;
    }

    .today-deliveries-empty {
      min-height: 35px;
      display: flex;
      align-items: center;
      color: #7D8985;
      font-size: 9px;
      font-weight: 600;
    }

    /* Filtro por loja do calendário */
    .calendar-store-filter {
      height: 30px;
      min-width: 145px;
      padding: 0 7px;
      display: inline-flex;
      align-items: center;
      gap: 5px;
      border: 1px solid #D7DEDC;
      border-radius: 7px;
      color: #5A6A65;
      background: #FFFFFF;
    }

    .calendar-store-filter svg {
      width: 13px;
      height: 13px;
      flex: 0 0 auto;
    }

    .calendar-store-filter select {
      width: 100%;
      min-width: 0;
      height: 28px;
      padding: 0 17px 0 0;
      border: 0;
      outline: 0;
      color: #374642;
      background: transparent;
      font-size: 9.5px;
      font-weight: 650;
    }

    /* Foto do cliente dentro do detalhe do calendário */
    .ops-event-item:has(.calendar-event-avatar) {
      grid-template-columns: 42px 34px minmax(0, 1fr);
    }

    .calendar-event-avatar {
      width: 30px;
      height: 30px;
      align-self: flex-start;
      margin-top: 1px;
      border-width: 1px;
      box-shadow: 0 0 0 1px #D6DFDC;
    }

    @media (max-width: 1280px) {
      .dashboard-welcome {
        display: grid;
      }

      .dashboard-highlight-row {
        width: 100%;
        justify-content: flex-start;
      }

      .birthday-dashboard-alert,
      .agenda-dashboard-widget,
      .today-deliveries-highlight {
        min-width: 0;
        flex: 1;
      }
    }

    @media (max-width: 850px) {
      .dashboard-highlight-row {
        display: grid;
        grid-template-columns: 1fr;
      }

      .birthday-dashboard-alert,
      .agenda-dashboard-widget,
      .today-deliveries-highlight {
        width: 100%;
      }

      .ops-calendar-top {
        align-items: flex-start;
        flex-direction: column;
        gap: 9px;
      }

      .ops-calendar-actions {
        width: 100%;
        flex-wrap: wrap;
      }

      .calendar-store-filter {
        min-width: 170px;
      }
    }

    @media (max-width: 520px) {
      .today-deliveries-highlight {
        padding: 8px;
      }

      .today-delivery-client {
        width: 44px;
        min-width: 44px;
      }

      .delivery-client-avatar {
        width: 32px;
        height: 32px;
      }

      .calendar-store-filter {
        width: 100%;
      }

      .ops-event-item:has(.calendar-event-avatar) {
        grid-template-columns: 34px 30px minmax(0, 1fr);
      }
    }


    .brand-lumix { color: var(--text-main); }
    .brand-opticas { display: inline-flex; align-items: baseline; letter-spacing: -0.045em; }
    .brand-o { color: #4285F4; }
    .brand-p { color: #EA4335; }
    .brand-t { color: #EA4335; }
    .brand-i { color: #FBBC04; }
    .brand-c { color: #34A853; }
    .brand-a { color: #4285F4; }
    .brand-s { color: #EA4335; }

    .brand-text {
      display: inline-flex;
      align-items: baseline;
      white-space: nowrap;
    }

    .topbar-left {
      min-width: 0 !important;
      gap: 14px !important;
    }

    .topbar-brand-navigation {
      min-width: 0;
      display: flex;
      align-items: center;
      gap: 8px;
      padding-right: 14px;
      border-right: 1px solid var(--border);
    }

    .topbar-brand-link {
      min-width: 0;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      color: inherit;
      text-decoration: none;
    }

    .topbar-brand-logo {
      width: 30px;
      height: 30px;
      display: grid;
      place-items: center;
      flex: 0 0 auto;
      overflow: hidden;
      border: 1px solid #dfe5e4;
      border-radius: 7px;
      background: #ffffff;
    }

    .topbar-brand-logo img {
      width: 23px;
      height: 23px;
      display: block;
      object-fit: contain;
    }

    .topbar-brand-name {
      display: inline-flex;
      align-items: baseline;
      color: var(--text-main);
      font-size: 14px;
      line-height: 1;
      font-weight: 800;
      letter-spacing: -0.035em;
      white-space: nowrap;
    }

    .back-home-btn {
      min-height: 30px;
      padding: 0 8px;
      display: inline-flex;
      align-items: center;
      gap: 5px;
      border: 1px solid #dce3e2;
      border-radius: 7px;
      color: #50605d;
      background: #ffffff;
      font-size: 9.5px;
      font-weight: 700;
      text-decoration: none;
      white-space: nowrap;
    }

    .back-home-btn svg {
      width: 13px;
      height: 13px;
    }

    .back-home-btn:hover {
      border-color: #bfcfcb;
      color: #254b49;
      background: #f4f8f7;
    }

    .route-context {
      min-width: 105px;
    }

    @media (max-width: 1180px) {
      .topbar-brand-name { display: none; }
      .topbar-brand-navigation { padding-right: 10px; }
      .back-home-btn span { display: none; }
      .back-home-btn { width: 30px; padding: 0; justify-content: center; }
    }

    @media (max-width: 820px) {
      .app-topbar { padding-left: 12px !important; padding-right: 12px !important; }
      .topbar-left { gap: 8px !important; }
      .topbar-brand-navigation { gap: 5px; padding-right: 7px; }
      .topbar-brand-logo { width: 28px; height: 28px; }
      .topbar-brand-logo img { width: 21px; height: 21px; }
      .route-context { min-width: 0; }
      .route-context span { display: none; }
      .route-context strong {
        max-width: 112px;
        overflow: hidden;
        font-size: 12px !important;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    @media (max-width: 560px) {
      .topbar-brand-logo { display: none; }
      .topbar-brand-navigation { border-right: 0; padding-right: 0; }
      .route-context { display: none; }
      .topbar-center { margin-left: 6px !important; margin-right: 6px !important; }
    }

  `}</style>
);
